/**
 * vfx.ts — render-layer VFX state + scheduling + drawing (v2M7.4a).
 *
 * Lives entirely in the render layer per GRAPHICS.md §11 invariant 4:
 * "No per-ship draw-state stored in sim. Explosion animation progress,
 * shimmer phase, muzzle-flash timers — all derived from tick number +
 * per-ship RNG, not stored." VFX state is session-scoped (lives across
 * frames but is recreated on each session start) and is held off
 * RenderContext.
 *
 * Pipeline per frame:
 *   1. `advanceVfx(state, gameState)` — diff against last-tick snapshot
 *      to detect new fire events; schedule a tracer per event; drop
 *      expired tracers. No-ops when `gameState.tick` hasn't advanced
 *      since the last call (rAF can fire many times between sim ticks).
 *   2. `drawVfx(ctx, state, gameState, transformWorldToScreen)` — paint
 *      every active tracer, fading by age in tick-time.
 *
 * v2M7.4a landed the *infrastructure* — diff-and-render pipeline plus
 * a generic white-line tracer.
 * v2M7.4b added §7.1 per-damage-type styling (Energy gradient beam,
 * Kinetic projectile + trail, Concussion slower projectile, EMP crackle).
 * v2M7.4c splits styling across all 7 weapon sub-types — Pulse vs Beam
 * (Energy), Burst vs Railgun (Kinetic), Missile vs Torpedo (Concussion),
 * and EMP — so the player can read which weapon fired at a glance even
 * within a damage category. Tracer lifetime also moves to per-sub-type
 * so e.g. a slow Torpedo lingers visibly longer than a fast Railgun
 * slug.
 * v2M7.5a adds the §4.5 destruction sequence. The same diff-and-render
 * pattern detects ships that vanish from `state.ships` with a non-null
 * prior `systemId` and schedules a 10-tick explosion VFX at the captured
 * death position: f0 white flash, f1–f3 silhouette fragments into 4–6
 * polygon shards exploding radially at 60 px/s, f4–f9 fade with
 * Warning-amber afterglow dots, f10 dropped. Shard direction is seeded
 * from `startTick ^ shipId` so the same death frame replays identically.
 * §7.3 drifting debris was scrapped during v2M7.5 wrap (see GRAPHICS.md).
 * v2M7.6a adds §8.1 jump-out and §8.3 jump-in animations. The pose
 * snapshot now also tracks each ship's `systemId`-presence; a non-null
 * → null transition fires JumpOutVfx (engine plume → line → bright dot
 * → dissipating trail, 17 ticks total), and a null → non-null transition
 * fires JumpInVfx (bright dot → line → silhouette + a 12 px Shield-blue
 * concentric ring, 6-tick body animation). Heading is captured from the
 * snapshotted moveOrder so the compress-to-line animation rotates
 * correctly even after the ship has vanished.
 * v2M7.6b (this revision) adds fog-of-war gating to drawVfx — every
 * VFX now stamps `ownerEmpireId` + the relevant `shipId` at schedule
 * time, and drawVfx skips VFX whose owning ship the viewer can't see
 * (`teamVision` row missing, or `seenVia !== 'vision'`). Plays for
 * own-empire ships unconditionally. Closes the v2M7.6a known
 * limitation where jump animations leaked enemy positions.
 */

import type {
  EmpireId,
  GameState,
  Missile,
  MissileId,
  ProjectileFamily,
  RaceId,
  ShipInstance,
  ShipInstanceId,
  SystemId,
  WeaponDef,
} from '../state/types/index';
import { RACE_ACCENT } from './ship-draw';
import {
  bezierAt,
  bezierTangentAt,
  easeMissile,
  missileFamilyFromWeaponName,
} from '../game/advance-missiles';
// v4M4.f.3 — sprite-driven weapon VFX path. Burst integration is the
// first family wired up; other families follow as their assets land.
// `getWeaponVfxSprite` returns null while assets are still loading or
// missing, so the procedural draws below stay as the fallback.
import {
  drawTintedSprite,
  getMkTierColour,
  getWeaponVfxSprite,
  parseMkIndex,
  pickAnimationFrame,
  withAdditiveBlend,
} from './weapon-vfx-sprites';

/** A single in-flight VFX. Discriminated union — extended each sub-checkpoint. */
export type VfxInstance =
  | TracerVfx
  | DestructionVfx
  | JumpOutVfx
  | JumpInVfx
  | FloatingTextVfx
  | MissileImpactVfx;

export interface TracerVfx {
  kind: 'tracer';
  /**
   * Weapon sub-type stamped from the firing weapon. v2M7.4c dispatches
   * per-sub-type styling on this field — all 7 of {Pulse, Beam, Burst,
   * Railgun, Missile, Torpedo, EMP} get distinct visuals so a player
   * can read which weapon fired at a glance even within a category.
   */
  subType: WeaponDef['subType'];
  /**
   * Damage type from the firing weapon. Forward-compat for impact effects
   * (shield ring flash on Energy hits, scorch decal on Kinetic, etc.) —
   * those need to know the damage class regardless of sub-type. v2M7.4c
   * doesn't read this from drawVfx; sim-side stamping keeps the field
   * available for whichever post-impact pass lands first.
   */
  damageType: 'Energy' | 'Kinetic' | 'Concussion' | 'EMP';
  /**
   * v4M4.f.3 — weapon name (e.g. "Energy Pulse Weapon Mk3"). Used by
   * the sprite renderer to derive the Mk tier for runtime tinting
   * (see `parseMkIndex` + `getMkTierColour` in weapon-vfx-sprites.ts).
   * Optional for backward-compat with snapshot-replays; renderer
   * falls back to neutral (untinted) when absent.
   */
  weaponName?: string;
  /**
   * Firer's ship-id and owner-empire — drives v2M7.6b fog-of-war gating
   * at draw time. The tracer renders only if the viewer can see the
   * firing ship; otherwise it'd leak position the same way an unfiltered
   * jump-in animation would.
   */
  firerShipId: ShipInstanceId;
  ownerEmpireId: EmpireId;
  /**
   * MFix5.8 — system this tracer belongs to. drawVfx skips tracers
   * whose system isn't the focused one so VFX from system A don't
   * bleed across when the user is viewing system B.
   */
  systemId: SystemId;
  /** Firer position in *world* coords (in-system 0..1 frame). */
  fromX: number;
  fromY: number;
  /** Target position in world coords, captured at scheduling time. */
  toX: number;
  toY: number;
  /** Sim tick at which this VFX was scheduled. */
  startTick: number;
  /** Duration in ticks. Drops from `state.active` once `tick - startTick >= durationTicks`. */
  durationTicks: number;
}

/**
 * Per-ship pose snapshot captured each tick. Used by destruction-event
 * detection to recover the ship's last-known position + identity after
 * sim has removed it from `gameState.ships`. Race + class get resolved
 * from `chassis` + `ownerEmpireId` at scheduling time so the snapshot
 * itself stays cheap to write.
 */
export interface ShipPoseSnapshot {
  id: ShipInstanceId;
  /** In-system world coords (0..1 frame) at snapshot tick. */
  sx: number;
  sy: number;
  /** Null while mid-jump; we only fire destruction VFX when this is non-null. */
  systemId: SystemId | null;
  ownerEmpireId: number;
  chassis: string;
  /**
   * Ship heading in radians (0 = +x axis, math convention), derived
   * from `moveOrder` if any, else 0. Captured into the snapshot so the
   * jump-out compress-to-line animation can rotate correctly even
   * after the ship has transitioned to mid-jump (and therefore lost
   * its in-system pose / move-order).
   */
  heading: number;
}

/**
 * Destruction VFX — 10-tick explosion played at the ship's last-known
 * position when it vanishes from `gameState.ships`. Per GRAPHICS §4.5:
 * f0 white flash, f1–f3 silhouette fragments into 4–6 shards at 60 px/s,
 * f4–f9 fade with Warning-amber afterglow, f10 dropped. RNG seeded
 * from `startTick ^ shipId` so a replay against the same destruction
 * frame produces the same shard distribution.
 */
export interface DestructionVfx {
  kind: 'destruction';
  /** Used as RNG seed component — different ships dying same tick get distinct shard fans. */
  shipId: ShipInstanceId;
  /** Drives v2M7.6b fog-of-war gating in drawVfx. */
  ownerEmpireId: EmpireId;
  /** MFix5.8 — system this VFX belongs to (drawVfx filters by focused system). */
  systemId: SystemId;
  /** Race accent or empire colour captured at scheduling time. */
  accentColor: string;
  /** Race id (drives shard motif if we ever specialise per-race). */
  raceId: RaceId;
  /** Ship class level (1..6). Drives shard size scaling. */
  classLevel: number;
  /** Death position in world coords (in-system 0..1 frame). */
  sx: number;
  sy: number;
  /** Tick at which the ship vanished. */
  startTick: number;
  /** Always 10 (1 second @ 10 Hz). Field kept for symmetry with TracerVfx. */
  durationTicks: number;
}

/**
 * Jump-out VFX — 17-tick warp departure animation per GRAPHICS §8.1.
 * Phases:
 *   - f0–f2 : engine plume elongates to 3× normal length, colour
 *             shifts toward white
 *   - f3–f4 : ship compresses to a line along its heading
 *   - f5    : line collapses to a bright dot
 *   - f6–f16: dot fades and a 1 s Hull-highlight trail dissipates
 *
 * Heading is captured from the snapshot's prior tick (because the ship
 * has transitioned to `systemId === null` by the time we schedule).
 */
export interface JumpOutVfx {
  kind: 'jumpout';
  shipId: ShipInstanceId;
  /** Drives v2M7.6b fog-of-war gating in drawVfx. */
  ownerEmpireId: EmpireId;
  /** MFix5.8 — origin system; drawVfx filters by focused system. */
  systemId: SystemId;
  accentColor: string;
  raceId: RaceId;
  classLevel: number;
  /** Last in-system position before transition to mid-jump. */
  sx: number;
  sy: number;
  /** Heading captured from prior snapshot's moveOrder. */
  heading: number;
  startTick: number;
  durationTicks: number;
}

/**
 * Jump-in VFX — 6-tick warp arrival animation per GRAPHICS §8.3 plus
 * a 5-tick Shield-blue concentric ring expanding from the arrival
 * point (0.5 s). Phases (body):
 *   - f0–f1 : bright dot at arrival point
 *   - f2–f3 : line expands along heading
 *   - f4–f5 : line resolves into ship silhouette + engine plume returns
 * The ring runs f0–f4 and fades on f5.
 */
export interface JumpInVfx {
  kind: 'jumpin';
  shipId: ShipInstanceId;
  /** Drives v2M7.6b fog-of-war gating in drawVfx. */
  ownerEmpireId: EmpireId;
  /** MFix5.8 — destination system; drawVfx filters by focused system. */
  systemId: SystemId;
  accentColor: string;
  raceId: RaceId;
  classLevel: number;
  /** Arrival point — ship's current sx/sy at the tick of jump-in. */
  sx: number;
  sy: number;
  /** Heading at arrival (from current moveOrder if any, else 0). */
  heading: number;
  startTick: number;
  durationTicks: number;
}

/**
 * v3M19 — floating damage-number VFX. Drained from
 * `gameState.combatEvents` each render frame (one combat event yields up
 * to three FloatingTextVfx — blue shield-portion damage, red hull-portion
 * damage, white `MISS ×K`). Rises a fixed pixel amount over its lifetime
 * with a fade on the last fraction of duration. FoW-gated at draw time
 * via the standard `canViewerSeeShip(targetShipId)` check.
 *
 * Display text is pre-formatted at scheduling time (e.g. `"42 ×3"` or
 * `"MISS ×2"` or `"42"` when the multiplier is 1). Colour is captured
 * as a CSS string so the renderer can paint without re-reading palette
 * constants per frame.
 *
 * Each event-derived floater is offset slightly from the others so they
 * don't stack visually — the renderer applies a small per-floater Y
 * offset based on `stackIndex` (0 = no offset, 1 = pushed up,
 * 2 = pushed up further). Three floaters max per event so the offset
 * scheme stays readable.
 */
export interface FloatingTextVfx {
  kind: 'floatingText';
  /**
   * Target ship's id at the time the volley resolved. Drives the FoW
   * gate (renderer drops floaters whose target the viewer can't see).
   * Captured separately from sx/sy so the floater anchors at the
   * volley-resolution position even if the ship moves or is despawned
   * between events landing and the render frame.
   */
  targetShipId: ShipInstanceId;
  /**
   * Firing empire's id. Currently unused at draw time (the FoW gate
   * keys on the target's visibility, not the firer's), but captured so
   * a future "always show damage I dealt" preview override can flip on
   * by checking `ownerEmpireId === viewer`.
   */
  ownerEmpireId: EmpireId;
  /** System the volley resolved in. drawVfx skips off-system floaters. */
  systemId: SystemId;
  /**
   * Anchor point in world coords (in-system 0..1 frame), captured at
   * volley-resolution time. Floater rises from here over its lifetime.
   */
  sx: number;
  sy: number;
  /** Pre-formatted display string ("42 ×3", "MISS ×2", or just "42" / "MISS"). */
  text: string;
  /** CSS colour. v3M19 palette: Shield-blue, Hull-red, or HUD-text-dim white. */
  colour: string;
  /**
   * 0..2 — vertical stack offset rank when one event yields multiple
   * floaters (shield + hull + miss). Renderer applies a per-rank Y
   * offset so the texts don't draw on top of each other.
   */
  stackIndex: number;
  startTick: number;
  durationTicks: number;
}

/**
 * v3M28.6 (post-playtest tune) — missile impact bloom. Spawned by
 * `advanceVfx` when a missile that was present last tick is gone this
 * tick AND its snapshot recorded `willHit === true`. Misses + target-
 * leaves-system early-despawns push no VFX (matches the audio side's
 * `prev.willHit` gate in `audio/diff.ts:428`). Family drives sprite
 * key + procedural fallback accent + peak diameter.
 *
 * Scale ramps 0 → 1.0 over the first 30% of life then holds at 1.0
 * (alpha covers the fade-out). Alpha is 1.0 for the first 30% then
 * linear-fades to 0 over the remaining 70%. 10-tick duration mirrors
 * `DestructionVfx`.
 *
 * `launcherShipId` is captured for the FoW gate at draw time — match
 * the launcher-keyed visibility that `drawMissiles` already enforces
 * during flight. Null when the launcher despawned before impact (rare
 * but permitted; falls back to "render iff own empire").
 */
export interface MissileImpactVfx {
  kind: 'missile-impact';
  /** v4M9.2.6 — `'torpedo'` joins the family enum so torpedo-arrival
   *  blooms share the same VFX kind + draw path; per-family peak px
   *  and accent live in the `MISSILE_IMPACT_*` records.
   *
   *  v4M9.3.4 — `'pulse'` added for the Pulse arrival flash (small
   *  energetic burst, no commissioned PNG — procedural circle only).
   *
   *  v4M9.4.4 — `'burst'` added for the Burst arrival flash (small
   *  kinetic spark per round; bursts hit in rapid succession so each
   *  individual flash needs to be small enough not to mass-saturate). */
  family: 'pulson' | 'zeon' | 'torpedo' | 'pulse' | 'burst';
  /** Drives v2M7.6b fog-of-war gating in drawVfx (via launcher visibility). */
  ownerEmpireId: EmpireId;
  /** MFix5.8 — system filter; drawVfx skips off-focused-system VFX. */
  systemId: SystemId;
  /** Launching ship id at fire time. Null if the launcher despawned mid-flight. */
  launcherShipId: ShipInstanceId | null;
  /** Impact position in world coords (target's sx1/sy1 from the missile's Bezier). */
  sx: number;
  sy: number;
  startTick: number;
  durationTicks: number;
}

/**
 * Per-missile snapshot held in `VfxState` so the next `advanceVfx`
 * can detect "missile arrived this tick" events and resolve the
 * impact bloom from the prior-tick state (the missile itself is gone
 * from `gameState.projectiles` by then). Mirrors `audio/diff.ts`'s
 * `prevMissileById` shape so the two layers stay in lockstep on what
 * counts as an impact vs a silent despawn.
 */
export interface MissileVfxSnapshot {
  systemId: SystemId;
  ownerEmpireId: EmpireId;
  launcherShipId: ShipInstanceId | null;
  willHit: boolean;
  /** Target's position at fire-time — fallback impact point when the
   *  target ship isn't found in `gameState.ships` at impact-detection
   *  time. Per the v3M28.6 post-playtest visual-homing update, the
   *  preferred impact-point source is the target ship's *current*
   *  sx/sy looked up via `targetShipId`; this fallback covers the
   *  case where the target died on the same tick the missile arrived
   *  (the kill removes the ship from `gameState.ships` before
   *  `advanceVfx` runs). */
  sx1: number;
  sy1: number;
  /** Target ship id — used at impact-detection time to read the
   *  target's current position, so the bloom lands on the target as
   *  it is now rather than where it was when the missile launched. */
  targetShipId: ShipInstanceId;
  /** Last-tick's target position snapshot — finer-grained fallback
   *  than `sx1, sy1` if the target died this tick. The render-side
   *  snapshot refresh captures this from the target ship at the end
   *  of each advanceVfx pass. */
  lastTargetSx: number;
  lastTargetSy: number;
  weaponName: string;
  /** For the "TTL elapsed → arrived" gate so cross-system early-despawns stay silent. */
  launchedAtTick: number;
  ttlTicks: number;
  /** v4M9.2.4 — projectile family captured from the live entity so the
   *  impact-bloom emitter can branch (Missile fires the chemical-
   *  detonation bloom; Torpedo currently skipped — torpedo impact
   *  bloom lands in v4M9.2.6). */
  family: ProjectileFamily;
}

/**
 * Render-layer VFX state. `active` holds in-flight VFX; `lastFiredByShip`
 * is the previous-tick snapshot of every ship's `lastFiredTick` so the
 * next advance can detect "ship X just fired" events. `lastShipSnapshot`
 * is a per-ship pose snapshot used by destruction-event detection to
 * recover position + identity after the ship is gone from sim state.
 * `lastMissileSnapshot` is the v3M28.6-tune-up analog for missile-impact
 * detection. `lastObservedTick` gates the diff so multiple render
 * frames within one sim tick don't schedule duplicate VFX.
 */
export interface VfxState {
  active: VfxInstance[];
  lastFiredByShip: Map<ShipInstanceId, number | null>;
  lastShipSnapshot: Map<ShipInstanceId, ShipPoseSnapshot>;
  lastMissileSnapshot: Map<MissileId, MissileVfxSnapshot>;
  lastObservedTick: number;
}

/**
 * Tracer lifetime per weapon sub-type, in sim ticks. Tick-time so VFX
 * scales with game speed. Tuned so the visual pace reads as "this is
 * how that weapon class feels":
 *
 *   - Pulse / Beam / EMP — instant-feel energy beams (4 ticks ≈ 400 ms
 *     fade). Pulse fires three quick flashes; Beam holds steady; EMP
 *     crackles. All four-tick lifetimes for that snappy energy read.
 *   - Burst — multi-shot kinetic, 7 ticks so the staggered shots can
 *     all leave the muzzle and travel some way before fade.
 *   - Railgun — single hyper-velocity slug, 5 ticks (just longer than
 *     Pulse so the contrail registers without overstaying).
 *   - Missile — guided projectile at 60 % kinetic speed, 8 ticks so
 *     it has visible flight time but still feels purposeful.
 *   - Torpedo — heavy slow ordnance, 12 ticks so it actually lumbers
 *     to target and reads as "this hits hard".
 */
const TRACER_DURATION_BY_SUBTYPE: Readonly<Record<TracerVfx['subType'], number>> = Object.freeze({
  Pulse: 4,
  Beam: 4,
  // MFix19 — Burst extended 7 → 14 so the streak persists long enough
  // to read as a sustained gatling stream rather than three quick
  // flashes. The 1.4 s duration at 10 Hz dovetails with the expanded
  // 7-tracer pattern in drawBurstSprite (a steady stream of rounds in
  // flight between firer and target).
  Burst: 14,
  Railgun: 5,
  Missile: 8,
  Torpedo: 12,
  EMP: 4,
});

/**
 * v4M4.f.5 Layer B — temporal stagger between same-tick fire events
 * from a single firer. Slot 0 fires at the canonical tick; each
 * subsequent slot delays by this many ticks. 2 ticks = 0.2 s at 10 Hz,
 * fast enough that a salvo still feels coordinated and slow enough
 * that the sweep is visible. The draw loop's `age < 0` skip handles
 * the not-yet-started window cleanly.
 */
const WEAPON_VFX_STAGGER_TICKS_PER_SLOT = 2;

/**
 * v4M4.f.5 Layer C — lateral mount spread per slot. 0.005 in 0..1
 * system-coord units ≈ a few px at typical tactical zoom, roughly
 * half a ship-radius. Multiplied by `mountOffsetForSlot(slotIndex)`
 * (alternating ±1, ±2, ...) to produce starboard/port spread off
 * the firer's centroid along the perpendicular to the fire vector.
 */
const WEAPON_VFX_MOUNT_SPREAD = 0.005;

/**
 * v4M4.f.5 Layer C heuristic — returns the lateral-offset multiplier
 * for the given weapon slot index:
 *
 *   slot 0 → 0   (centroid — single-weapon ships unaffected)
 *   slot 1 → +1  (starboard)
 *   slot 2 → -1  (port)
 *   slot 3 → +2  (further starboard)
 *   slot 4 → -2  (further port)
 *   ...
 *
 * The resulting alternating spread reads as multiple weapon mounts on
 * a ship's hull. Future v4M4.f.6 can replace this with authored
 * per-chassis mount data.
 */
function mountOffsetForSlot(slotIndex: number): number {
  if (slotIndex === 0) return 0;
  const distance = Math.ceil(slotIndex / 2);
  const sign = slotIndex % 2 === 1 ? 1 : -1;
  return distance * sign;
}

/**
 * Missile impact bloom lifetime. MFix11.6.1 (2026-05-18) — reduced
 * from 10 → 6 ticks (0.6 s @ 10 Hz). v3M28.6 calibrated this against
 * a sparse missile-only playfield; v4M4.f playtest with Burst +
 * Railgun + Beam tracers in the same scene showed the 1.0 s bloom
 * dominating combat visuals. The shorter duration keeps the impact
 * legible without drowning out tracers from other weapon families.
 */
const MISSILE_IMPACT_DURATION_TICKS = 6;

/**
 * Peak diameter per missile family for the impact bloom, in screen
 * pixels. MFix11.6.1 (2026-05-18) — reduced ~30 % across both
 * families (pulson 20 → 14, zeon 32 → 22). v3M28.6 sized these to
 * roughly 2× projectile sprite footprint, but combat-density playtest
 * showed the blooms overlap aggressively during salvo volleys and
 * mask other weapons' tracers. The shockwave ring (`peakPx * 1.6`)
 * shrinks proportionally; combined with the duration cut, missile
 * impacts now register as bright moments rather than dominant scene
 * fixtures.
 */
const MISSILE_IMPACT_PEAK_PX: Record<
  'pulson' | 'zeon' | 'torpedo' | 'pulse' | 'burst',
  number
> = {
  pulson: 14,
  zeon: 22,
  // v4M9.2.6 — Torpedo impact is heavier than Zeon — slow projectile,
  // big payload. The 28 px peak reads as a noticeable flash without
  // overwhelming the combat read at salvo density.
  torpedo: 28,
  // v4M9.3.4 — Pulse impact is small and frequent. 8 px peak keeps the
  // arrival flash readable per-bolt without overwhelming the combat
  // read at Pulse-volume (hundreds of bolts per tick in big fights).
  pulse: 8,
  // v4M9.4.4 — Burst impact is per-round (3-6 per burst, staggered).
  // 6 px peak keeps each round's flash punctuated rather than blending
  // into a single mass at the impact site.
  burst: 6,
};

/**
 * Procedural-fallback accent per missile family — used when the
 * impact PNG hasn't loaded yet, or load failed, so the bloom still
 * registers visually instead of silently no-op-ing. Same hex pairs
 * as the projectile body accents in `drawMissileEntity` so the
 * family read stays consistent.
 */
const MISSILE_IMPACT_FALLBACK_ACCENT: Record<
  'pulson' | 'zeon' | 'torpedo' | 'pulse' | 'burst',
  string
> = {
  pulson: '#f4c042',
  zeon: '#e6743a',
  // v4M9.2.6 — torpedo accent matches the v4M4.f.3 plasma-core cohort
  // (cyan-white core, blue-orange halo). Cyan-white reads as energetic
  // plasma vs. the warmer chemical-detonation accents.
  torpedo: '#9ee8ff',
  // v4M9.3.4 — pulse accent matches the v4M4.f.3 pulse-bolt cohort
  // (energy-blue). Same hex Energy damage uses across the audio +
  // tracer layers so the cross-modal read stays consistent.
  pulse: '#7ec8ff',
  // v4M9.4.4 — burst accent matches the v4M4.f.3 burst-spark cohort
  // (kinetic-orange, warm muzzle-flash hue). Reads as gunfire spark
  // at impact, distinct from the cool energy palettes of Pulse/Torpedo.
  burst: '#ffb86b',
};

/**
 * Destruction sequence: 10 sim ticks (= 1 s @ 10 Hz) per GRAPHICS §4.5.
 * Each tick is a "frame" in the spec's f0..f9 schedule.
 */
const DESTRUCTION_DURATION_TICKS = 10;

/**
 * Jump-out: 7-frame core animation + 10-frame trail dissipation
 * (1 s Hull-highlight trail per §8.1) = 17 ticks total.
 */
const JUMP_OUT_DURATION_TICKS = 17;

/** Jump-in: 6-frame body animation per §8.3. Ring runs f0–f4 inside. */
const JUMP_IN_DURATION_TICKS = 6;

/** Shield-blue ring expansion duration during jump-in (0.5 s @ 10 Hz). */
const JUMP_IN_RING_DURATION_TICKS = 5;

/**
 * v3M19 — floating-damage-number lifetime: 12 ticks (1.2 s @ 10 Hz).
 * Tick-time so the visual scales with game speed. Long enough to read
 * comfortably during a 2× / 4× speed-up; short enough that dense combat
 * doesn't bury the play area in stacked floaters.
 */
const FLOATING_TEXT_DURATION_TICKS = 12;

/**
 * v3M19 — floating-damage-number palette. Blue for shield-portion damage
 * (Shield-blue per GRAPHICS §2.1), red for hull-portion damage (Hull-red),
 * dim white for MISS / DODGE tags (HUD-text-dim). Defined as CSS strings
 * so drawFloatingText doesn't have to re-resolve palette names per frame.
 */
const FLOATING_TEXT_SHIELD_COLOUR = 'rgba(120, 180, 255, 1)';
const FLOATING_TEXT_HULL_COLOUR = 'rgba(255, 100, 100, 1)';
const FLOATING_TEXT_MISS_COLOUR = 'rgba(220, 220, 220, 1)';

/**
 * v3M19 — pre-format the per-event display text. Damage numbers round
 * to nearest integer (no decimals — the precise float values are noise
 * in player-facing UI); the `×N` multiplier is suppressed when only one
 * round contributed (the common case for single-shot weapons).
 */
function formatDamageText(amount: number, multiplier: number): string {
  const rounded = Math.round(amount);
  return multiplier > 1 ? `${rounded} ×${multiplier}` : `${rounded}`;
}

function formatMissText(missCount: number): string {
  return missCount > 1 ? `MISS ×${missCount}` : 'MISS';
}

/** Per-shard radial speed in screen-space pixels per second (GRAPHICS §4.5). */
const SHARD_SPEED_PX_PER_SEC = 60;
const SIM_TICK_HZ = 10;

/** Palette pulled from GRAPHICS §2.1 + §7.1. */
const ENERGY_YELLOW = '#FFD84D';
const HULL_HIGHLIGHT = '#C5CEDB';
const WARNING_AMBER = '#F0A93B';
const EMP_CYAN = '#4FB3FF';
// MFix27 — HUD_TEXT_DIM removed; sole consumer was the muzzle puff in the
// pre-MFix27 volley-model drawBurstKinetic, which the per-projectile
// rewrite dropped (muzzle flash would now require a separate WeaponFireEvent
// path at the firer's position rather than the projectile's).
/** Shield-blue: same hex as EMP_CYAN per §2.1 (#4FB3FF). Aliased for readability. */
const SHIELD_BLUE = EMP_CYAN;

export function createVfxState(): VfxState {
  return {
    active: [],
    lastFiredByShip: new Map(),
    lastShipSnapshot: new Map(),
    lastMissileSnapshot: new Map(),
    lastObservedTick: -1,
  };
}

/**
 * True if a JumpInVfx is currently animating for this ship at `tick`.
 * Used by `system-view.ts` to suppress the regular ship draw for the
 * 6 ticks the arrival animation owns the silhouette — otherwise the
 * fully-resolved race silhouette would render at full opacity behind
 * the bright-dot / line phases of the VFX.
 */
export function isShipInJumpIn(state: VfxState, shipId: ShipInstanceId, tick: number): boolean {
  for (const v of state.active) {
    if (v.kind !== 'jumpin') continue;
    if (v.shipId !== shipId) continue;
    const age = tick - v.startTick;
    if (age >= 0 && age < v.durationTicks) return true;
  }
  return false;
}

/**
 * Detect new fire events and schedule tracers; drop expired tracers.
 * Idempotent within a single sim tick (no-op when `gameState.tick` hasn't
 * advanced) so rAF can call it freely without double-spawning VFX.
 *
 * v3M19.3 — `opts.showDamageNumbers` (default true) gates the
 * floating-damage-numbers drain. When false, the renderer skips
 * scheduling FloatingTextVfx instances; the sim still publishes
 * DamageEvents into `gameState.combatEvents` (cheap; cleared next
 * tick by `advanceCombat`).
 */
export interface AdvanceVfxOpts {
  showDamageNumbers?: boolean;
}

export function advanceVfx(
  state: VfxState,
  gameState: GameState,
  opts: AdvanceVfxOpts = {},
): void {
  // Drop expired tracers per their type-specific duration. Each VFX
  // carries `durationTicks` in its own row so the filter is type-aware.
  state.active = state.active.filter((v) => gameState.tick - v.startTick < v.durationTicks);

  // Skip event detection when the sim hasn't advanced since the last
  // call. Without this guard the rAF render loop would re-spawn VFX
  // every render frame between sim ticks.
  if (gameState.tick === state.lastObservedTick) return;
  state.lastObservedTick = gameState.tick;

  // Destruction detection — ships present in last tick's pose snapshot
  // but absent from this tick's `gameState.ships`. We only fire VFX
  // when the prior systemId was non-null (i.e., the ship was actually
  // in-system, not mid-jump). A mid-jump ship that vanishes is either
  // completing its jump (no VFX) or being scrubbed for some other
  // reason (also no VFX in v2M7.5a — corner-case acceptable per spec).
  for (const [id, snap] of state.lastShipSnapshot) {
    if (gameState.ships.has(id)) continue;
    if (snap.systemId === null) continue;
    // MFix13.3b/.3c — graceful despawns (carrier squadron recall, carrier-
    // jump auto-recall) skip the destruction VFX. The squadron isn't
    // destroyed, it docked at the carrier; rendering a shards-and-bloom
    // explosion would be misleading.
    if (gameState.gracefulDespawnIds?.has(id)) continue;
    state.active.push(buildDestructionVfx(snap, gameState));
  }

  // v3M28.6 (post-playtest tune) — missile-impact detection. Missile
  // ids present in `lastMissileSnapshot` but gone from current
  // `gameState.projectiles` mean the advance pass despawned the missile.
  // Three despawn causes:
  //   (a) Arrival on a willHit-true missile → fire the impact bloom.
  //   (b) Arrival on a willHit-false missile → silent (matches the
  //       audio diff's `!prev.willHit` gate).
  //   (c) Target died / left system mid-flight → also silent. We
  //       discriminate (a) from (c) by checking whether TTL had
  //       actually elapsed at the despawn tick. Early-despawns leave
  //       (launchedAtTick + ttlTicks > current tick), so (a) requires
  //       `launchedAtTick + ttlTicks <= gameState.tick`.
  for (const [id, prev] of state.lastMissileSnapshot) {
    if (gameState.projectiles.has(id)) continue;
    if (!prev.willHit) continue;
    if (prev.launchedAtTick + prev.ttlTicks > gameState.tick) continue;
    // v4M9.2.6 — per-family bloom family key. Missile resolves to its
    // sub-family ('pulson' | 'zeon') from the weapon name; Torpedo
    // maps straight to the 'torpedo' family entry in the
    // MISSILE_IMPACT_PEAK_PX / FALLBACK_ACCENT records. The draw path
    // (drawMissileImpact) dispatches off the same family value.
    //
    // v4M9.3.4 — Pulse maps to 'pulse' (small + frequent flash).
    // v4M9.4.4 — Burst maps to 'burst' (small kinetic spark, fires
    //            per-round so multiple flashes punctuate per burst).
    const family: 'pulson' | 'zeon' | 'torpedo' | 'pulse' | 'burst' =
      prev.family === 'Torpedo'
        ? 'torpedo'
        : prev.family === 'Pulse'
          ? 'pulse'
          : prev.family === 'Burst'
            ? 'burst'
            : missileFamilyFromWeaponName(prev.weaponName);
    // Bloom position: target's current sx/sy if still alive; else the
    // last-tick snapshot (the kill removed it from gameState.ships
    // before advanceVfx ran); else the fire-time sx1/sy1 (deepest
    // fallback, used only if the snapshot fields are absent, which
    // shouldn't happen but the type permits).
    const target = gameState.ships.get(prev.targetShipId);
    const impactSx = target ? target.sx : prev.lastTargetSx;
    const impactSy = target ? target.sy : prev.lastTargetSy;
    state.active.push({
      kind: 'missile-impact',
      family,
      ownerEmpireId: prev.ownerEmpireId,
      systemId: prev.systemId,
      launcherShipId: prev.launcherShipId,
      sx: impactSx,
      sy: impactSy,
      startTick: gameState.tick,
      durationTicks: MISSILE_IMPACT_DURATION_TICKS,
    });
  }

  // Jump-state transition detection — pairs of (snap.systemId, ship.systemId)
  // tell us whether a ship just left its system (jump-out fires §8.1) or
  // just arrived (jump-in fires §8.3). New ships (no prior snapshot) are
  // skipped to avoid an erroneous jump-in animation on session start.
  for (const ship of gameState.ships.values()) {
    const snap = state.lastShipSnapshot.get(ship.id);
    if (!snap) continue;
    const wasInSystem = snap.systemId !== null;
    const isInSystem = ship.systemId !== null;
    if (wasInSystem && !isInSystem) {
      state.active.push(buildJumpOutVfx(snap, gameState));
    } else if (!wasInSystem && isInSystem) {
      state.active.push(buildJumpInVfx(ship, gameState));
    }
  }

  // MFix11.5 + v4M4.f.5 — drain per-weapon-slot fire events from the
  // combat pass. Each event becomes one TracerVfx with the correct
  // subType for its weapon — mixed-loadout ships now render N tracers
  // per fire-tick (one per fired slot) rather than the v2M7.4c "first
  // weapon stands in for the whole ship" approximation.
  //
  // v4M4.f.5 layered the Excalibur-style sweep effect on top:
  //   - Layer B (temporal stagger): tracers from slots 1..N stagger
  //     by `WEAPON_VFX_STAGGER_TICKS_PER_SLOT` ticks per slot, so
  //     a 3× Beam ship visibly sweeps beam 0 → 1 → 2 over multiple
  //     ticks rather than firing all three at the same instant. The
  //     draw loop at line ~947 (`if (age < 0) continue`) naturally
  //     hides the staggered tracer until its adjusted start tick is
  //     reached.
  //   - Layer C (per-mount origin): tracers from different slots
  //     emerge from perpendicular-offset positions around the firer,
  //     so multiple beams visibly emanate from different hull points
  //     rather than all from the centroid. Heuristic: alternating
  //     starboard/port spread, expanding distance per slot. Future
  //     work (v4M4.f.6) can replace this with authored per-chassis
  //     mount-point data.
  //
  // Events for this tick are populated by `advance-combat.ts` and
  // cleared at the start of the next combat pass; the
  // `lastObservedTick` gate above ensures we only drain once per tick.
  for (const event of gameState.weaponFireEvents) {
    // v3M28.4 — Missile subtype renders from `state.projectiles`
    // directly via `drawMissiles` below; suppress the legacy tracer
    // push so we don't double-draw a fast linear-lerp dot on top of
    // the new Bezier-eased persistent missile.
    //
    // v4M9.2.4 — Torpedo joined the persistent-projectile pipeline
    // too. Same double-draw suppression rule applies; the v4M4.f.3
    // plasma-core/halo cohort renders from inside `drawMissiles` on
    // the `family === 'Torpedo'` branch instead of via TracerVfx.
    //
    // v4M9.3.4 — Pulse joined the persistent-projectile pipeline.
    // Same filter rule: the v4M4.f.3 pulse-bolt sprite renders from
    // inside `drawMissiles` on the `family === 'Pulse'` branch.
    //
    // v4M9.4.4 — Burst joined too. Each fire-event used to schedule
    // a single tracer; under the new model the per-round Projectile
    // entries (multiple per fire-event, staggered) own the render.
    //
    // v4M9.5.6 — Beam joins. Sustained beam channels render from
    // `state.beams` via `drawBeams`; no transient TracerVfx needed.
    if (
      event.subType === 'Missile' ||
      event.subType === 'Torpedo' ||
      event.subType === 'Pulse' ||
      event.subType === 'Burst' ||
      event.subType === 'Beam'
    ) {
      continue;
    }
    // TracerVfx damageType excludes 'EMP' (EMP uses a separate
    // crackle visual). Map EMP back to Concussion for the damage-
    // type slot — matches the v3M4 convention where EMP weapons
    // carry damageType: 'Concussion' under the hood.
    const damageType = event.damageType === 'EMP' ? 'Concussion' : event.damageType;
    // v4M4.f.5 Layer C — perpendicular mount-offset heuristic. The
    // fire direction defines a forward axis; the perpendicular (90°
    // CCW) is the lateral axis. Slot 0 fires from the centroid
    // (offsetMag = 0); slots 1, 3, 5 ... offset right; slots 2, 4,
    // 6 ... offset left; distance grows ceil(slotIndex/2) units.
    const dx = event.toX - event.fromX;
    const dy = event.toY - event.fromY;
    const len = Math.hypot(dx, dy) || 1;
    const pdx = -dy / len; // perpendicular unit-X (90° CCW)
    const pdy = dx / len;  // perpendicular unit-Y
    const offsetMag = mountOffsetForSlot(event.slotIndex) * WEAPON_VFX_MOUNT_SPREAD;
    const fromX = event.fromX + pdx * offsetMag;
    const fromY = event.fromY + pdy * offsetMag;
    // v4M4.f.5 Layer B — temporal stagger. Slot 0 fires at the sim
    // tick; subsequent slots delay by N × STAGGER ticks each. The
    // draw loop hides a tracer with age < 0 until its adjusted
    // startTick is reached.
    const startTick = event.tick + event.slotIndex * WEAPON_VFX_STAGGER_TICKS_PER_SLOT;
    state.active.push({
      kind: 'tracer',
      subType: event.subType,
      damageType,
      weaponName: event.weaponName,
      firerShipId: event.shipId,
      ownerEmpireId: event.ownerEmpireId,
      systemId: event.systemId,
      fromX,
      fromY,
      toX: event.toX,
      toY: event.toY,
      startTick,
      // v4M9.6 — Railgun reads its tracer duration from
      // `state.weaponMechanics.railgun[name].visualSustainTicks` per
      // the v4M9.0 design lock. Railgun is the hyper-velocity-slug
      // exception: damage stays instant-hit (the `applyVolleyToShip`
      // path), but the tracer needs an extended on-screen lifetime
      // so the hit reads visually rather than blinking past the
      // viewer's eye in 5 ticks. Other subTypes use the fixed
      // `TRACER_DURATION_BY_SUBTYPE` table (the migrated families
      // continue past this point only as the `EMP` defensive-fallback
      // branch above, so the table is effectively EMP + Railgun only
      // post-v4M9.5; Railgun is the per-Mk-tunable one).
      durationTicks:
        event.subType === 'Railgun'
          ? gameState.weaponMechanics.railgun[event.weaponName]?.visualSustainTicks ??
            TRACER_DURATION_BY_SUBTYPE.Railgun
          : TRACER_DURATION_BY_SUBTYPE[event.subType],
    });
  }

  // Drop snapshot rows for ships that have despawned, so the lastFired
  // map doesn't grow unbounded across long sessions.
  if (state.lastFiredByShip.size > gameState.ships.size + 32) {
    const aliveIds = new Set(gameState.ships.keys());
    for (const id of state.lastFiredByShip.keys()) {
      if (!aliveIds.has(id)) state.lastFiredByShip.delete(id);
    }
  }

  // v3M19 — drain `gameState.combatEvents` into FloatingTextVfx
  // instances. Each event (one per slot fire from the combat / Surface
  // Battery / ODP passes) yields up to three floaters: blue for the
  // shield-portion damage, red for the hull-portion, white for the
  // miss tally. Floaters with zero amount are suppressed (no
  // "0" floater on a volley that landed entirely on shields, no MISS
  // floater on a perfectly-accurate volley). `stackIndex` ranks the
  // emitted floaters per event so the renderer can offset them
  // vertically and they don't draw on top of each other.
  // v3M19.3 — gated on the gameplay setting; when off, sim still
  // publishes events but the renderer doesn't schedule any floaters.
  const showDamageNumbers = opts.showDamageNumbers ?? true;
  if (showDamageNumbers) {
    for (const evt of gameState.combatEvents) {
    let stackIndex = 0;
    if (evt.shieldDamage > 0) {
      state.active.push({
        kind: 'floatingText',
        targetShipId: evt.targetShipId,
        ownerEmpireId: evt.ownerEmpireId,
        systemId: evt.systemId,
        sx: evt.sx,
        sy: evt.sy,
        text: formatDamageText(evt.shieldDamage, evt.hitCount),
        colour: FLOATING_TEXT_SHIELD_COLOUR,
        stackIndex: stackIndex++,
        startTick: gameState.tick,
        durationTicks: FLOATING_TEXT_DURATION_TICKS,
      });
    }
    if (evt.hullDamage > 0) {
      state.active.push({
        kind: 'floatingText',
        targetShipId: evt.targetShipId,
        ownerEmpireId: evt.ownerEmpireId,
        systemId: evt.systemId,
        sx: evt.sx,
        sy: evt.sy,
        text: formatDamageText(evt.hullDamage, evt.hitCount),
        colour: FLOATING_TEXT_HULL_COLOUR,
        stackIndex: stackIndex++,
        startTick: gameState.tick,
        durationTicks: FLOATING_TEXT_DURATION_TICKS,
      });
    }
    if (evt.missCount > 0) {
      state.active.push({
        kind: 'floatingText',
        targetShipId: evt.targetShipId,
        ownerEmpireId: evt.ownerEmpireId,
        systemId: evt.systemId,
        sx: evt.sx,
        sy: evt.sy,
        text: formatMissText(evt.missCount),
        colour: FLOATING_TEXT_MISS_COLOUR,
        stackIndex: stackIndex++,
        startTick: gameState.tick,
        durationTicks: FLOATING_TEXT_DURATION_TICKS,
      });
    }
    }
  }

  // MFix42.5b (2026-06-01) — drain `gameState.bombardmentEvents` into
  // DestructionVfx-style explosion entries anchored at the planet
  // surface. The destruction VFX shape gives a polygon-shard burst
  // already proven for ship deaths; reusing it keeps the bombardment
  // visual coherent with the rest of the combat VFX language without
  // an extra sprite cohort.
  //
  // Damage-type → accent colour mapping:
  //   Energy     → cool blue-white (#5ad4ff)  — laser/beam impact
  //   Kinetic    → warm orange     (#ffb86b)  — railgun / projectile
  //   Concussion → fire red        (#ff6450)  — missile / torpedo
  //
  // Synthetic shipId for RNG seed variety: top bit set + planetId so
  // it can't collide with any real ShipInstanceId. raceId + classLevel
  // get sensible defaults — the destruction-VFX renderer only uses
  // classLevel for shard size scaling (3 = medium) and raceId for an
  // unimplemented future per-race shard motif.
  for (const evt of gameState.bombardmentEvents ?? []) {
    const empire = gameState.empires.get(evt.ownerEmpireId);
    let accentColor: string;
    switch (evt.damageType) {
      case 'Energy':     accentColor = '#5ad4ff'; break;
      case 'Kinetic':    accentColor = '#ffb86b'; break;
      case 'Concussion': accentColor = '#ff6450'; break;
    }
    state.active.push({
      kind: 'destruction',
      shipId: (0x40000000 | evt.planetId) as ShipInstanceId,
      ownerEmpireId: evt.ownerEmpireId,
      systemId: evt.systemId,
      accentColor,
      raceId: empire?.raceId ?? 'RACE-AA',
      classLevel: 3,
      sx: evt.sx,
      sy: evt.sy,
      startTick: evt.tick,
      durationTicks: DESTRUCTION_DURATION_TICKS,
    });
  }

  // Refresh the pose snapshot for next tick's destruction + jump-state
  // detection. Replace wholesale rather than merge — rows for despawned
  // ships simply don't survive the rebuild, which doubles as cleanup.
  state.lastShipSnapshot.clear();
  for (const ship of gameState.ships.values()) {
    state.lastShipSnapshot.set(ship.id, {
      id: ship.id,
      sx: ship.sx,
      sy: ship.sy,
      systemId: ship.systemId,
      ownerEmpireId: ship.ownerEmpireId,
      chassis: ship.chassis,
      heading: computeShipHeading(ship),
    });
  }

  // v3M28.6 (post-playtest tune) — refresh per-missile snapshot for
  // next tick's impact detection. Same wholesale-rebuild pattern as
  // ship pose; despawned missile rows fall away naturally. Per the
  // visual-homing update, we also capture the target ship's CURRENT
  // sx/sy so the next-tick impact detection can blit the bloom at the
  // target's last-known position even if the target dies on the
  // impact tick (and so vanishes from `gameState.ships` before
  // `advanceVfx` runs).
  state.lastMissileSnapshot.clear();
  for (const missile of gameState.projectiles.values()) {
    const target = gameState.ships.get(missile.targetShipId);
    const lastTargetSx = target ? target.sx : missile.sx1;
    const lastTargetSy = target ? target.sy : missile.sy1;
    state.lastMissileSnapshot.set(missile.id, {
      systemId: missile.systemId,
      ownerEmpireId: missile.ownerEmpireId,
      launcherShipId: missile.launchedFromShipId,
      willHit: missile.willHit,
      sx1: missile.sx1,
      sy1: missile.sy1,
      targetShipId: missile.targetShipId,
      lastTargetSx,
      lastTargetSy,
      weaponName: missile.weaponName,
      launchedAtTick: missile.launchedAtTick,
      ttlTicks: missile.ttlTicks,
      family: missile.family,
    });
  }
}

/**
 * Heading from a ship's `moveOrder` — atan2 of the delta vector toward
 * its target. Same idle default (`-π/2` = world-up) as system-view.ts
 * so an unanchored jump-out / jump-in animation aligns with the ship's
 * visual orientation when the ship was idle.
 */
function computeShipHeading(ship: ShipInstance): number {
  if (!ship.moveOrder) return -Math.PI / 2;
  const dx = ship.moveOrder.targetX - ship.sx;
  const dy = ship.moveOrder.targetY - ship.sy;
  if (dx === 0 && dy === 0) return -Math.PI / 2;
  return Math.atan2(dy, dx);
}

/**
 * Resolve the per-ship visual identity (race / class / accent colour)
 * that destruction + jump VFX all need. Empire colour is the per-slot
 * ownership colour and is the most identity-evident pick — two
 * same-race empires fighting each other get distinguishable VFX.
 * Falls back to RACE_ACCENT then Hull-highlight if the empire row is
 * gone (rare but possible if the empire died the same tick).
 */
function lookupShipVisuals(
  ownerEmpireId: number,
  chassis: string,
  gameState: GameState,
): { accentColor: string; raceId: RaceId; classLevel: number } {
  const empire = gameState.empires.get(ownerEmpireId);
  const raceId: RaceId = empire?.raceId ?? 'RACE-AA';
  const tmpl = gameState.shipTemplates.get(`${raceId}:${chassis}`);
  const accentColor = empire?.color ?? RACE_ACCENT[raceId] ?? HULL_HIGHLIGHT;
  return { accentColor, raceId, classLevel: tmpl?.classLevel ?? 1 };
}

/**
 * Build a DestructionVfx from a ship-pose snapshot. Caller has already
 * checked `snap.systemId !== null` (mid-jump destructions are skipped),
 * so the non-null assertion is safe.
 */
function buildDestructionVfx(
  snap: ShipPoseSnapshot,
  gameState: GameState,
): DestructionVfx {
  const v = lookupShipVisuals(snap.ownerEmpireId, snap.chassis, gameState);
  return {
    kind: 'destruction',
    shipId: snap.id,
    ownerEmpireId: snap.ownerEmpireId,
    systemId: snap.systemId!, // MFix5.8 — drawVfx system filter
    ...v,
    sx: snap.sx,
    sy: snap.sy,
    startTick: gameState.tick,
    durationTicks: DESTRUCTION_DURATION_TICKS,
  };
}

/**
 * Build a JumpOutVfx from a snapshot of the ship's last in-system tick.
 * Caller checks `wasInSystem` (snap.systemId !== null) before invoking.
 */
function buildJumpOutVfx(snap: ShipPoseSnapshot, gameState: GameState): JumpOutVfx {
  const v = lookupShipVisuals(snap.ownerEmpireId, snap.chassis, gameState);
  return {
    kind: 'jumpout',
    shipId: snap.id,
    ownerEmpireId: snap.ownerEmpireId,
    systemId: snap.systemId!, // MFix5.8 — drawVfx system filter
    ...v,
    sx: snap.sx,
    sy: snap.sy,
    heading: snap.heading,
    startTick: gameState.tick,
    durationTicks: JUMP_OUT_DURATION_TICKS,
  };
}

/**
 * Build a JumpInVfx from the ship's current arrival pose. Caller checks
 * `isInSystem` (ship.systemId !== null) before invoking.
 */
function buildJumpInVfx(ship: ShipInstance, gameState: GameState): JumpInVfx {
  const v = lookupShipVisuals(ship.ownerEmpireId, ship.chassis, gameState);
  return {
    kind: 'jumpin',
    shipId: ship.id,
    ownerEmpireId: ship.ownerEmpireId,
    systemId: ship.systemId!, // MFix5.8 — drawVfx system filter
    ...v,
    sx: ship.sx,
    sy: ship.sy,
    heading: computeShipHeading(ship),
    startTick: gameState.tick,
    durationTicks: JUMP_IN_DURATION_TICKS,
  };
}

// MFix11.5 — `inferWeaponForShip` + `categoryToDamageType` retired
// alongside the per-weapon TracerVfx attribution migration. The new
// scheduler drains `state.weaponFireEvents` directly, which already
// carry per-slot subType/damageType from the combat pass — no
// loadout-walking guess needed. `VfxState.lastFiredByShip` and its
// GC block in `advanceVfx` are also now orphan and can be removed in
// a follow-up cleanup pass.

/**
 * True if the viewer can currently see the given ship. Mirrors the
 * fog-of-war check `system-view.ts` applies to ship rendering — own
 * ships always visible; other empires' ships only if `teamVision` has
 * an active vision row (not scan / not ghost). v2M7.6b uses this to
 * gate VFX so a hidden ship's destruction / jump / muzzle flash
 * doesn't leak its position.
 */
function canViewerSeeShip(
  gameState: GameState,
  viewerId: EmpireId,
  shipId: ShipInstanceId,
  ownerEmpireId: EmpireId,
): boolean {
  if (ownerEmpireId === viewerId) return true;
  const viewerVision = gameState.teamVision.get(viewerId);
  if (!viewerVision) return false;
  const rec = viewerVision.get(shipId);
  return rec?.seenVia === 'vision';
}

/**
 * Paint every active VFX. `transform` converts world-space (in-system
 * 0..1 frame) to screen pixels — passed in so vfx.ts doesn't pull in
 * camera / viewport types. `viewerEmpireId` (v2M7.6b) drives fog-of-war
 * gating: VFX whose owning ship the viewer can't see are skipped, so
 * an explosion / jump / muzzle flash never reveals an unseen enemy.
 */
export function drawVfx(
  ctx: CanvasRenderingContext2D,
  state: VfxState,
  gameState: GameState,
  viewerEmpireId: EmpireId,
  transform: (worldX: number, worldY: number) => { x: number; y: number },
  // v3M13 — devRevealAll bypasses the FoW gate so weapons-fire from any
  // empire renders. Defaults false so non-renderer callers (tests) keep
  // the strict gate.
  revealAll: boolean = false,
  // MFix5.8 — system filter. Each VFX entry now stamps its origin
  // system; only those matching the focused system render. Null means
  // galaxy view (no system focused) — no in-system VFX should draw.
  focusedSystemId: SystemId | null = null,
): void {
  const tick = gameState.tick;
  for (const v of state.active) {
    const age = tick - v.startTick;
    if (age < 0 || age >= v.durationTicks) continue;
    // MFix5.8 — system filter. Skip VFX that don't belong to the
    // currently-focused system (prevents weapons-fire from system A
    // bleeding into system B's view after the user switches).
    if (focusedSystemId === null || v.systemId !== focusedSystemId) continue;
    // FoW gating — every VFX kind carries an "owning ship" id whose
    // visibility gates whether the VFX renders. For tracer it's the
    // firer; for destruction / jumpout / jumpin it's the subject of
    // the animation (the dying / jumping ship); for floatingText it's
    // the target receiving the damage (so floaters drop when the
    // viewer can't see who's being shot, even if they can see the
    // attacker).
    // v3M28.6 (post-playtest tune) — MissileImpactVfx has no
    // single "subject ship": the launcher may be dead and the target
    // certainly is (or was). Gate on launcher visibility when the
    // launcher id was captured at fire-time, else fall back to
    // "render iff own empire" (same fallback `drawMissiles` uses for
    // in-flight missiles with a null launcher).
    if (v.kind === 'missile-impact') {
      if (!revealAll) {
        if (v.launcherShipId !== null) {
          if (!canViewerSeeShip(gameState, viewerEmpireId, v.launcherShipId, v.ownerEmpireId)) {
            continue;
          }
        } else if (v.ownerEmpireId !== viewerEmpireId) {
          continue;
        }
      }
      const c = transform(v.sx, v.sy);
      drawMissileImpact(ctx, c, age, v);
      continue;
    }
    let owningShipId: ShipInstanceId;
    // MFix11.6.3 — `ownerForFoW` must be the owner of the ship whose
    // visibility gates the VFX. For tracer / destruction / jump VFX
    // that's `v.ownerEmpireId` (the firer's / subject's empire,
    // captured at schedule time). For floatingText the gating ship is
    // the TARGET, and the target's owner empire isn't `v.ownerEmpireId`
    // (which v3M19 wires to the ATTACKER's empire for a future "show
    // damage I dealt" override that was never built). Look up the
    // target ship live so the player's own ships taking damage trip
    // the `canViewerSeeShip` shortcut (`ownerEmpireId === viewerId →
    // true`) instead of falling through to the teamVision check that
    // (correctly) reports no entry for the player's own ships.
    let ownerForFoW: EmpireId;
    if (v.kind === 'tracer') {
      owningShipId = v.firerShipId;
      ownerForFoW = v.ownerEmpireId;
    } else if (v.kind === 'floatingText') {
      owningShipId = v.targetShipId;
      const targetShip = gameState.ships.get(v.targetShipId);
      ownerForFoW = targetShip?.ownerEmpireId ?? v.ownerEmpireId;
    } else {
      owningShipId = v.shipId;
      ownerForFoW = v.ownerEmpireId;
    }
    if (
      !revealAll &&
      !canViewerSeeShip(gameState, viewerEmpireId, owningShipId, ownerForFoW)
    ) {
      continue;
    }
    const ageFrac = age / v.durationTicks;
    if (v.kind === 'tracer') {
      const a = transform(v.fromX, v.fromY);
      const b = transform(v.toX, v.toY);
      switch (v.subType) {
        case 'Pulse':
          // v4M9.3.4 — Pulse migrated to the persistent-projectile
          // pipeline; the upstream filter suppresses Pulse subType
          // events from reaching this switch. Defensive no-op.
          break;
        case 'Beam':
          // v4M9.5.6 — Beam migrated to the persistent-beam-sustain
          // pipeline; the upstream filter suppresses Beam subType
          // events from reaching this switch. Defensive no-op.
          break;
        case 'Burst':
          // v4M9.4.4 — Burst migrated to the persistent-projectile
          // pipeline; the upstream filter suppresses Burst subType
          // events from reaching this switch. Defensive no-op.
          break;
        case 'Railgun':
          if (!drawRailgunSprite(ctx, a, b, ageFrac, v.weaponName)) {
            drawRailgunKinetic(ctx, a, b, ageFrac);
          }
          break;
        case 'Missile':
        case 'Torpedo':
          // v4M9.2.6 — Missile + Torpedo no longer schedule tracer
          // VFX (the persistent-projectile pipeline at `drawMissiles`
          // owns their rendering). The upstream weaponFireEvents
          // filter skips both subTypes before they reach this switch,
          // so these cases are defensive only. Drop the legacy
          // tracer-style draws (`drawMissileConcussion`,
          // `drawTorpedoSprite`/`drawTorpedoConcussion`); any stray
          // event lands here and silently no-ops rather than blitting
          // a duplicate transient on top of the persistent sprite.
          break;
        case 'EMP':
          drawEmpCrackle(ctx, a, b, ageFrac, v.startTick);
          break;
      }
    } else if (v.kind === 'destruction') {
      const c = transform(v.sx, v.sy);
      drawDestruction(ctx, c, age, v);
    } else if (v.kind === 'jumpout') {
      const c = transform(v.sx, v.sy);
      drawJumpOut(ctx, c, age, v);
    } else if (v.kind === 'jumpin') {
      const c = transform(v.sx, v.sy);
      drawJumpIn(ctx, c, age, v);
    } else if (v.kind === 'floatingText') {
      const c = transform(v.sx, v.sy);
      drawFloatingText(ctx, c, age, v);
    }
  }

  // v3M28.4 — persistent missile entities. Each in-flight missile is
  // drawn from its Bezier triple + eased age; sprite assets land in
  // v3M28.6 but the procedural placeholder here gives per-family
  // colour + size differentiation (Pulson small amber, Zeon larger
  // red-orange) so playtest can validate the swarm read while assets
  // are still in the pipe.
  drawMissiles(ctx, gameState, viewerEmpireId, transform, revealAll, focusedSystemId);
  // v4M9.5.6 — render in-flight beam-sustain channels. Iterates
  // `state.beams` rather than `state.projectiles`; beams are a
  // separate entity collection per v4M9.1a's data-model split.
  drawBeams(ctx, gameState, viewerEmpireId, transform, revealAll, focusedSystemId);
}

/**
 * v4M9.5.6 — render in-flight Beam-sustain channels. Each `BeamFire`
 * in `state.beams` draws a beam line from the firer's current
 * `(sx, sy)` to the target's current `(sx, sy)` (re-targeted each
 * frame so the beam visibly tracks a moving target).
 *
 * Visual phases mirror the sim:
 *
 *   - **Charge-up** (`tick < startTick + chargeUpTicks`): visible beam
 *     line, attenuated alpha — reads as the weapon spinning up. No sim
 *     damage applies during this phase (see `advanceBeams`).
 *   - **Sustain** (during damage application): full-intensity beam.
 *   - **Done**: entry is gone from `state.beams`; nothing to draw.
 *
 * FoW: visibility keyed on the firer's ship id (same `canViewerSeeShip`
 * gate the projectile renderer uses for `launchedFromShipId`).
 */
function drawBeams(
  ctx: CanvasRenderingContext2D,
  gameState: GameState,
  viewerEmpireId: EmpireId,
  transform: (worldX: number, worldY: number) => { x: number; y: number },
  revealAll: boolean,
  focusedSystemId: SystemId | null,
): void {
  if (focusedSystemId === null) return;
  for (const beam of gameState.beams.values()) {
    if (beam.systemId !== focusedSystemId) continue;

    // FoW gate via firer visibility. If the firer has despawned mid-
    // sustain (e.g., counter-fire killed them while the beam was
    // channelling) we fall back to "own beam renders, others don't"
    // via owner empire match — beam continues to channel per the
    // current `advanceBeams` contract (the target validation aborts
    // on target loss, not firer loss).
    if (!revealAll) {
      const firer = gameState.ships.get(beam.firerShipId);
      if (firer) {
        if (
          !canViewerSeeShip(gameState, viewerEmpireId, beam.firerShipId, beam.ownerEmpireId)
        ) {
          continue;
        }
      } else if (beam.ownerEmpireId !== viewerEmpireId) {
        continue;
      }
    }

    // Beam endpoints — re-read each frame so the visible beam tracks
    // moving ships. If either end has despawned, skip (the next sim
    // tick will resolve the channel through `advanceBeams`).
    const firer = gameState.ships.get(beam.firerShipId);
    const target = gameState.ships.get(beam.targetShipId);
    if (!firer || !target) continue;

    const totalDuration = beam.chargeUpTicks + beam.sustainTicks;
    const age = gameState.tick - beam.startTick;
    if (age < 0 || age >= totalDuration) continue;
    // ageFrac for `drawBeamSprite` runs 0..1 across the full channel
    // (charge-up + sustain). The sprite's internal phase animation
    // can interpret early fractions as wind-up.
    const ageFrac = totalDuration > 0 ? age / totalDuration : 0;

    // MFix12.1 — pull the beam endpoints in by `BEAM_ENDPOINT_OFFSET_PX`
    // along the firer→target axis so the line connects sprite-edge to
    // sprite-edge rather than passing through both ships' centres.
    // Without this offset, the beam appears to start "behind" the firer
    // (the firer's sprite extends in the direction of fire past the
    // ship's `(sx, sy)` centre) and "through" the target. Done in screen
    // space because ship sprites are zoom-invariant pixel-sized (see
    // v4M4.a SHIP_SPRITE_RENDER_SCALE) — a fixed-pixel offset stays
    // visually correct at any zoom level. If a degenerate case ever
    // collapses the firer and target to the same screen point, skip
    // the offset (the beam would render as a zero-length line anyway).
    const rawA = transform(firer.sx, firer.sy);
    const rawB = transform(target.sx, target.sy);
    const ddx = rawB.x - rawA.x;
    const ddy = rawB.y - rawA.y;
    const dlen = Math.hypot(ddx, ddy);
    const a =
      dlen > BEAM_ENDPOINT_OFFSET_PX * 2
        ? {
            x: rawA.x + (ddx / dlen) * BEAM_ENDPOINT_OFFSET_PX,
            y: rawA.y + (ddy / dlen) * BEAM_ENDPOINT_OFFSET_PX,
          }
        : rawA;
    const b =
      dlen > BEAM_ENDPOINT_OFFSET_PX * 2
        ? {
            x: rawB.x - (ddx / dlen) * BEAM_ENDPOINT_OFFSET_PX,
            y: rawB.y - (ddy / dlen) * BEAM_ENDPOINT_OFFSET_PX,
          }
        : rawB;
    // MFix19 — apply a perpendicular offset per slotIndex so multiple
    // Beam channels from the same firer don't collapse to a single
    // overlapping line. The offset emanates from the firer endpoint
    // (a) but tapers to zero at the target (b) so the channels still
    // converge on the hit point — reads as multiple emitters firing
    // at the same target rather than parallel beams missing past it.
    // Perpendicular unit vector: (-ddy, ddx) / dlen.
    const aOffset = beamSlotPerpOffsetPx(beam.slotIndex);
    if (dlen > 0 && aOffset !== 0) {
      const px = -ddy / dlen;
      const py = ddx / dlen;
      a.x += px * aOffset;
      a.y += py * aOffset;
    }
    // MFix26 — sprite path removed. The cropped manual-cohort sprite
    // never read as a beam-of-light despite multiple tunes (MFix17,
    // MFix19, MFix20, MFix23, MFix24, MFix25). The procedural draw is
    // a clean 2 px gradient line that's hard to make bad — accept it
    // as v1 and revisit sprite-based beams when better art is sourced.
    // Mk-tier colour now plumbed into drawBeamEnergy directly.
    const beamTierColour = getMkTierColour('beam', parseMkIndex(beam.weaponName));
    drawBeamEnergy(ctx, a, b, ageFrac, beamTierColour);
  }
}

/**
 * MFix12.1 — screen-pixel offset for beam endpoint inset.
 *
 * MFix23 — settled at 20 after live observation. Earlier values:
 *   24 (MFix12.1 baseline at SHIP_SPRITE_RENDER_SCALE 0.20)
 *   → 40 (MFix19 bump for post-task-205 0.30 scale)
 *   → 70 (MFix20 "decisively clear" attempt)
 *   → 20 (MFix23, current — observed in-game that 70 launched beams
 *         off the ship sprite entirely, ~3 ship-lengths into empty
 *         space. Ships render at fixed screen-pixel size regardless
 *         of zoom, half-extent ~30-40 px; 20 puts the inset just
 *         forward of centerline, on the prow of the visible sprite).
 */
const BEAM_ENDPOINT_OFFSET_PX = 20;

/**
 * MFix19 — perpendicular pixel offset for the firer endpoint, keyed
 * to the BeamFire's slotIndex. Slot 0 → 0 (centered); slots 1+ fan
 * out in alternating signed steps so e.g. 4-Beam loadout indices
 * 0,1,2,3 produce offsets 0, +14, -14, +28. Caps at SPREAD_MAX_PX
 * either side so very large loadouts don't push emitter points off
 * the sprite. The offset only applies to the firer endpoint — the
 * target endpoint stays at the hit point so all channels converge.
 */
function beamSlotPerpOffsetPx(slotIndex: number): number {
  if (slotIndex <= 0) return 0;
  // MFix23 — tightened from STEP 14, SPREAD 36 (which sent slot-5
  // launches sqrt(20² + 36²) ≈ 41 px perpendicular, well off-sprite
  // when combined with the previous 70 px axial inset). New values
  // map slots 1..5 to ±6, ±12, ±18 px — wing positions on the
  // visible ship raster (half-width ≈ 30-40 px). 6+ slots cap at
  // ±18 rather than fanning further. With BEAM_ENDPOINT_OFFSET_PX
  // = 20, max launch is sqrt(20² + 18²) ≈ 27 px from ship center,
  // firmly inside the sprite.
  const STEP_PX = 6;
  const SPREAD_MAX_PX = 18;
  // 1 → +6, 2 → -6, 3 → +12, 4 → -12, 5+ → ±18 …
  const magnitude = Math.ceil(slotIndex / 2) * STEP_PX;
  const sign = slotIndex % 2 === 1 ? 1 : -1;
  return Math.min(SPREAD_MAX_PX, magnitude) * sign;
}

/**
 * v3M28.4 — render every in-flight missile in the focused system.
 *
 * Each missile is drawn at its Bezier-eased position rotated to face
 * the tangent. A short fading trail of "exhaust puffs" is sampled
 * backward along the missile's recent path to give a smoke-streak
 * read at swarm scale — without the puffs, multiple missiles on
 * arcing paths still read as discrete dots; with them, every missile
 * leaves a distinct ribbon.
 *
 * FoW gating: the missile inherits the launcher's visibility. If the
 * launcher isn't visible (or has despawned), the missile doesn't
 * render — same posture as tracer FoW.
 */
function drawMissiles(
  ctx: CanvasRenderingContext2D,
  gameState: GameState,
  viewerEmpireId: EmpireId,
  transform: (worldX: number, worldY: number) => { x: number; y: number },
  revealAll: boolean,
  focusedSystemId: SystemId | null,
): void {
  if (focusedSystemId === null) return;
  const tick = gameState.tick;
  for (const missile of gameState.projectiles.values()) {
    if (missile.systemId !== focusedSystemId) continue;
    // FoW: visibility keyed on launcher. If launcher is null
    // (defensive — shouldn't happen mid-flight under v3M28.0 rules
    // but the type permits it), fall back to "own missile renders,
    // others don't" via owner empire match.
    if (!revealAll) {
      const launcherId = missile.launchedFromShipId;
      if (launcherId !== null) {
        if (!canViewerSeeShip(gameState, viewerEmpireId, launcherId, missile.ownerEmpireId)) {
          continue;
        }
      } else if (missile.ownerEmpireId !== viewerEmpireId) {
        continue;
      }
    }
    const ageFrac = (tick - missile.launchedAtTick) / missile.ttlTicks;
    if (ageFrac < 0) continue; // launched this tick but tick hasn't ticked yet
    if (ageFrac > 1) continue; // past arrival; advance pass will despawn next tick
    // v3M28.6 (post-playtest tune) — visual homing. Re-target the
    // Bezier end point to the target ship's current position each
    // frame so the missile visibly tracks a moving target. Sim-side
    // accuracy (`willHit`) is still rolled at fire-time per
    // MISSILES.md §3 — this is purely a visual update. Fall back to
    // the missile's fire-time `sx1, sy1` if the target is gone (rare:
    // typically the missile despawns silently before draw runs, but
    // there can be a one-render-frame gap).
    const target = gameState.ships.get(missile.targetShipId);
    let visualSx1: number;
    let visualSy1: number;
    if (target) {
      visualSx1 = target.sx;
      visualSy1 = target.sy;
      // Miss-scatter: for willHit === false, nudge the visual end
      // point perpendicular to the launcher→target line so misses
      // *look* like misses (the missile arcs past the target rather
      // than appearing to detonate on it with no bloom). Magnitude
      // + sign derived deterministically from missile.id so replays
      // produce identical scatter; range ≈ 0.015..0.030 in the
      // 0..1 in-system frame (roughly 1.5 % – 3 % of system width).
      if (!missile.willHit) {
        const h = (missile.id * 2654435761) >>> 0;
        const sign = h & 1 ? 1 : -1;
        const mag = 0.015 + ((h >>> 1) % 256) / 256 * 0.015;
        const dx = visualSx1 - missile.sx0;
        const dy = visualSy1 - missile.sy0;
        const len = Math.hypot(dx, dy) || 1;
        // Perpendicular unit vector (rotate (dx,dy) by 90°).
        const perpX = -dy / len;
        const perpY = dx / len;
        visualSx1 += perpX * sign * mag;
        visualSy1 += perpY * sign * mag;
      }
    } else {
      // Target gone — keep original fire-time endpoint. Missile is
      // likely about to despawn silently on the next advance.
      visualSx1 = missile.sx1;
      visualSy1 = missile.sy1;
    }
    // Shallow-clone the missile with retargeted endpoint so all
    // downstream bezier evaluations (current pos, tangent, smoke-
    // puff backstep positions in drawMissileEntity) share the same
    // visual arc. Cheap object allocation per missile per frame —
    // typical in-flight count is single digits per system.
    const visualMissile: Missile = { ...missile, sx1: visualSx1, sy1: visualSy1 };
    const t = easeMissile(ageFrac);
    const pos = bezierAt(visualMissile, t);
    const tan = bezierTangentAt(visualMissile, t);
    const screenPos = transform(pos.x, pos.y);
    if (missile.family === 'Torpedo') {
      // v4M9.2.4 — persistent Torpedo render. Plasma-core + halo sprite
      // (v4M4.f.3 cohort) drawn at the Bezier-evaluated position with
      // orientation along the tangent. No smoke-puff trail — the chemical-
      // propellant trail is Missile-family-specific. `drawTorpedoSprite`
      // expects a (start, end) pair so it can derive its own lerp +
      // angle; we synthesise a one-pixel-ahead end-point from the
      // tangent.
      //
      // MFix20.1 — pass the missile's actual `ageFrac` (0..1) instead
      // of a hard 0. The function uses ageFrac for two things: a
      // position lerp `a + (b-a)*ageFrac` (effectively a sub-pixel
      // drift here because `screenAhead` is only ~0.01 world units
      // ahead of `screenPos`) and the travel-sprite frame index
      // `floor(ageFrac * 3)`. The pre-MFix20.1 hard 0 fixed the frame
      // index to 0 and the player saw a single static torpedo image
      // across the entire flight. With the actual ageFrac, the three
      // travel frames cycle once across the flight (0 → 0.33 → 0.66 → end).
      const tanLen = Math.hypot(tan.x, tan.y) || 1;
      const aheadWorld = {
        x: pos.x + (tan.x / tanLen) * 0.01,
        y: pos.y + (tan.y / tanLen) * 0.01,
      };
      const screenAhead = transform(aheadWorld.x, aheadWorld.y);
      drawTorpedoSprite(
        ctx,
        screenPos,
        screenAhead,
        ageFrac,
        missile.weaponName,
        gameState.tick,
      );
      continue;
    }
    if (missile.family === 'Pulse') {
      // v4M9.3.4 — persistent Pulse render. The v4M4.f.3 pulse-bolt
      // sprite drawn at the linearly-interpolated position. Falls
      // back to the procedural `drawPulseEnergy` if the sprite hasn't
      // loaded.
      //
      // MFix28.4 — straight-line lerp (no Bezier) so target movement
      // doesn't curve the bolt path.
      //
      // MFix29.1 — re-anchor the visible spawn to the firer's CURRENT
      // position each frame. Pre-MFix29.1 the rendered position was
      // a lerp from missile.sx0/sy0 (the firer's LAUNCH-time position
      // plus slot offset). If the firer moved forward toward target
      // between launch tick and the current frame, the visible spawn
      // sat at the firer's PAST position — i.e. behind the firer
      // ("bolt fires from behind the ship"). Now: compute spawn each
      // frame as `firer.current + perp_unit × slotPerp` using the
      // current firer→target geometry. Falls back to launch-time
      // sx0/sy0 if the firer has despawned mid-flight.
      const t = easeMissile(ageFrac);
      const firer =
        missile.launchedFromShipId !== null
          ? gameState.ships.get(missile.launchedFromShipId)
          : undefined;
      let spawnX: number;
      let spawnY: number;
      if (firer) {
        const fcDx = missile.sx1 - firer.sx;
        const fcDy = missile.sy1 - firer.sy;
        const fcLen = Math.hypot(fcDx, fcDy) || 1;
        const perpUx = -fcDy / fcLen;
        const perpUy = fcDx / fcLen;
        spawnX = firer.sx + perpUx * missile.slotPerp;
        spawnY = firer.sy + perpUy * missile.slotPerp;
      } else {
        // Firer despawned — fall back to stored launch-time spawn
        // (which is the firer centerline; slotPerp not applied here
        // because we can't recover the launch-time perpendicular
        // axis without the firer reference).
        spawnX = missile.sx0;
        spawnY = missile.sy0;
      }
      const pulseHomedX = spawnX + (missile.sx1 - spawnX) * t;
      const pulseHomedY = spawnY + (missile.sy1 - spawnY) * t;
      const pulseHomedScreenPos = transform(pulseHomedX, pulseHomedY);
      const pulseDx = missile.sx1 - spawnX;
      const pulseDy = missile.sy1 - spawnY;
      const pulseLen = Math.hypot(pulseDx, pulseDy) || 1;
      const pulseAheadWorld = {
        x: pulseHomedX + (pulseDx / pulseLen) * 0.01,
        y: pulseHomedY + (pulseDy / pulseLen) * 0.01,
      };
      const pulseScreenAhead = transform(
        pulseAheadWorld.x,
        pulseAheadWorld.y,
      );
      if (
        !drawPulseSprite(
          ctx,
          pulseHomedScreenPos,
          pulseScreenAhead,
          0,
          missile.weaponName,
        )
      ) {
        drawPulseEnergy(ctx, pulseHomedScreenPos, pulseScreenAhead, 0);
      }
      continue;
    }
    if (missile.family === 'Burst') {
      // MFix27 — sprite path removed (same rationale as MFix26 retired
      // drawBeamSprite). Sprite-driven Burst inherited a stale volley-
      // model render that drew 7 collapsed tracers at the projectile's
      // own position; procedural per-projectile render is cleaner and
      // doesn't require asset-loading state.
      //
      // MFix28.3 — bypass the shared visualMissile/Bezier path entirely
      // for Burst. The shared path retargets sx1/sy1 to current target
      // position each frame (a homing-visual mechanic that other
      // families want) but leaves cx/cy at the LAUNCH-time midpoint —
      // pairing a stale midpoint with a moving endpoint produces a
      // curved trajectory. With multi-slot Burst loadouts each slot's
      // curve differs (different launch-spawn → different stale
      // midpoint), so rounds visibly diverge toward the target end of
      // the trajectory: V pointing wrong way ("fire spreads outward").
      // Kinetic bullets don't home — fly each round straight from its
      // own slot-offset launch-spawn to its launch-time aim point in a
      // simple lerp, eased the same way other projectile families ease.
      const t = easeMissile(ageFrac);
      const homedX = missile.sx0 + (missile.sx1 - missile.sx0) * t;
      const homedY = missile.sy0 + (missile.sy1 - missile.sy0) * t;
      const homedScreenPos = transform(homedX, homedY);
      const burstDx = missile.sx1 - missile.sx0;
      const burstDy = missile.sy1 - missile.sy0;
      const burstLen = Math.hypot(burstDx, burstDy) || 1;
      const burstAheadWorld = {
        x: homedX + (burstDx / burstLen) * 0.01,
        y: homedY + (burstDy / burstLen) * 0.01,
      };
      const burstScreenAhead = transform(
        burstAheadWorld.x,
        burstAheadWorld.y,
      );
      drawBurstKinetic(ctx, homedScreenPos, burstScreenAhead, 0);
      continue;
    }
    const subFamily = missileFamilyFromWeaponName(missile.weaponName);
    drawMissileEntity(ctx, screenPos, tan, subFamily, missile.id, ageFrac, visualMissile, transform);
  }
}

// ---------------------------------------------------------------------------
// v3M28.6 — sprite-load scaffolding for the missile assets
//
// The Stability.ai assets (per `docs/ART_PROMPTS.md §3.1`) load via
// `getMissileSprite(key)` — returns `HTMLImageElement | null` once
// loading finishes, `null` until then or after a permanent load
// failure. `drawMissileEntity` below checks the result and falls
// through to the procedural draw whenever a sprite isn't available,
// so the render path stays green even when assets are missing.
//
// Path convention: `/vfx/missile/<key>.png`. Place the keepers at
// `public/vfx/missile/` (or symlink the existing
// `assets/art/vfx/missile/` to that location) so Vite serves them
// at the root. Alternatively call `setMissileSpriteSrc(key, url)`
// before the first render to override the URL — useful if the
// integration step uses `import url from '...?url'` instead.
//
// Keys: `pulson-projectile`, `zeon-projectile`, `smoke-puff`,
// `pulson-impact`, `zeon-impact`. These map 1:1 to the registry
// rows in `docs/ART_PROMPTS.md §3.1`.
// ---------------------------------------------------------------------------

type MissileSpriteKey =
  | 'pulson-projectile'
  | 'zeon-projectile'
  | 'smoke-puff'
  | 'pulson-impact'
  | 'zeon-impact';

interface MissileSpriteSlot {
  /** Image instance once loading kicks off; never reassigned after init. */
  image: HTMLImageElement | null;
  /** `true` once load succeeds OR fails — final state, no further attempts. */
  resolved: boolean;
  /** `true` only on successful load; `false` on `onerror` or unsupported env. */
  ok: boolean;
}

const missileSpriteSlots: Map<MissileSpriteKey, MissileSpriteSlot> = new Map();
const missileSpriteSrcOverrides: Map<MissileSpriteKey, string> = new Map();

/**
 * v3M28.6 integration hook — override the URL the loader uses for a
 * given sprite key. Useful when the integration step prefers
 * `import url from './path.png?url'` (Vite-rewrites it to a hashed
 * URL) instead of the default `/vfx/missile/<key>.png` convention.
 *
 * Call BEFORE the first frame that would attempt the load. After
 * the load kicks off the override has no effect.
 */
export function setMissileSpriteSrc(key: MissileSpriteKey, url: string): void {
  missileSpriteSrcOverrides.set(key, url);
}

function getMissileSprite(key: MissileSpriteKey): HTMLImageElement | null {
  const cached = missileSpriteSlots.get(key);
  if (cached) {
    return cached.resolved && cached.ok ? cached.image : null;
  }
  // Headless guard — vitest 'node' env doesn't define Image; bail
  // immediately rather than throw.
  if (typeof Image === 'undefined') {
    missileSpriteSlots.set(key, { image: null, resolved: true, ok: false });
    return null;
  }
  const image = new Image();
  const slot: MissileSpriteSlot = { image, resolved: false, ok: false };
  missileSpriteSlots.set(key, slot);
  image.onload = () => {
    slot.resolved = true;
    slot.ok = image.naturalWidth > 0 && image.naturalHeight > 0;
  };
  image.onerror = () => {
    slot.resolved = true;
    slot.ok = false;
  };
  image.src = missileSpriteSrcOverrides.get(key) ?? `/vfx/missile/${key}.png`;
  return null;
}

/** Family-specific missile draw. Sprite-driven when v3M28.6 assets
 *  are present (auto-detected via `getMissileSprite` above); falls
 *  through to procedural canvas drawing when they're not. */
function drawMissileEntity(
  ctx: CanvasRenderingContext2D,
  screenPos: { x: number; y: number },
  tangent: { x: number; y: number },
  family: 'pulson' | 'zeon',
  missileId: number,
  ageFrac: number,
  missile: Missile,
  transform: (worldX: number, worldY: number) => { x: number; y: number },
): void {
  const isZeon = family === 'zeon';
  const accent = isZeon ? '#E04A24' : '#F0A93B'; // red-orange vs amber
  const flame = isZeon ? '#FFE2A8' : '#FFE6B2';
  const projectileRadius = isZeon ? 3.5 : 2.5;
  // MFix11.6.1 (2026-05-18) — in-flight trail reduced ~33 % (zeon
  // 18→12, pulson 12→8) alongside the impact-bloom tuning. Long
  // trails during salvo volleys overlapped into a continuous smoke
  // ribbon that masked tracers from other weapon families; shorter
  // trails read as discrete missile flights without the visual
  // density.
  const trailLen = isZeon ? 12 : 8;

  // v3M28.6 sprite-or-procedural selector. The procedural fallback
  // is intentionally kept — if assets are missing or fail to load,
  // playtest still works, just with the placeholder visuals.
  const projectileSprite = getMissileSprite(
    isZeon ? 'zeon-projectile' : 'pulson-projectile',
  );
  const smokeSprite = getMissileSprite('smoke-puff');

  // Smoke-puff trail: sample 4 backward steps along the path,
  // fading out. Each puff is at an earlier eased-t with progressive
  // opacity falloff. Renders BEFORE the projectile so the puffs sit
  // visually behind it. Sprite-driven when `smoke-puff.png` is
  // loaded; procedural grey dot when it isn't.
  const puffCount = 4;
  // v3M28.6 playtest signal — trail read as a fat impact bloom at
  // 1.8× projectile radius. Halved to 0.9× to match the projectile
  // sprite reduction and keep the trail wisp-y rather than smoke-cloud.
  const puffSize = projectileRadius * 0.9;
  for (let i = 1; i <= puffCount; i++) {
    const backstep = i * 0.04; // 4% of TTL per puff = ~1.2 ticks @ 30-tick TTL
    const pastFrac = Math.max(0, ageFrac - backstep);
    if (pastFrac <= 0) break;
    const pastT = easeMissile(pastFrac);
    const pastPos = bezierAt(missile, pastT);
    const pastScreen = transform(pastPos.x, pastPos.y);
    const alpha = (1 - i / (puffCount + 1)) * 0.4;
    ctx.save();
    ctx.globalAlpha = alpha;
    if (smokeSprite) {
      ctx.drawImage(
        smokeSprite,
        pastScreen.x - puffSize,
        pastScreen.y - puffSize,
        puffSize * 2,
        puffSize * 2,
      );
    } else {
      ctx.fillStyle = '#8a8a8a';
      ctx.beginPath();
      ctx.arc(pastScreen.x, pastScreen.y, projectileRadius * 0.9, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // Rotation from tangent. The tangent vector points along the
  // direction of travel; atan2 gives the rotation angle.
  const angle = Math.atan2(tangent.y, tangent.x);

  ctx.save();
  ctx.translate(screenPos.x, screenPos.y);
  ctx.rotate(angle);

  // v3M28.6 sprite path — if the projectile sprite loaded, blit it
  // rotated + scaled to match the procedural footprint. Sprite size
  // is twice the procedural radius (`projectileRadius * 2` per axis)
  // so the rendered footprint stays consistent across the sprite /
  // procedural toggle and existing playtest signal on size is
  // preserved.
  if (projectileSprite) {
    // v3M28.6 playtest signal — Pulson 15 / Zeon 21 px screen
    // footprint at multiplier 6 read too large against ship glyphs.
    // Dropped to 3 (Pulson 7.5 / Zeon 10.5 px), keeping the sprite
    // clearly larger than the procedural fallback radius
    // (projectileRadius * 1.5) so the visual differentiation between
    // sprite-loaded and procedural-fallback paths stays legible.
    const spriteSize = projectileRadius * 3;
    ctx.drawImage(
      projectileSprite,
      -spriteSize / 2,
      -spriteSize / 2,
      spriteSize,
      spriteSize,
    );
    ctx.restore();
    void flame; // reserved for fallback path; unused on sprite path
    void missileId;
    return;
  }

  // ── Procedural fallback path ─────────────────────────────────────
  // Exhaust streak — tapered triangle behind the projectile, hot in
  // the centre.
  const streak = ctx.createLinearGradient(0, 0, -trailLen, 0);
  streak.addColorStop(0, accent);
  streak.addColorStop(0.6, accent);
  streak.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = streak;
  ctx.beginPath();
  ctx.moveTo(0, -projectileRadius * 0.7);
  ctx.lineTo(-trailLen, -projectileRadius * 0.15);
  ctx.lineTo(-trailLen, projectileRadius * 0.15);
  ctx.lineTo(0, projectileRadius * 0.7);
  ctx.closePath();
  ctx.fill();

  // Projectile body — elongated along the travel axis. Nose-forward
  // teardrop, hot inner core.
  ctx.fillStyle = accent;
  ctx.beginPath();
  ctx.ellipse(0, 0, projectileRadius * 1.5, projectileRadius, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = flame;
  ctx.beginPath();
  ctx.arc(projectileRadius * 0.7, 0, projectileRadius * 0.55, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Suppress unused-arg warnings — missileId is reserved for future
  // per-missile rng-driven puff jitter (v3M28.7 polish).
  void missileId;
}

/**
 * Pulse (Energy sub-type): three quick yellow dot-flashes travelling
 * firer → target along the beam axis, staggered so they read as a
 * "rapid-fire pulse" rather than one steady beam. Distinct from Beam,
 * which holds a single solid line for its lifetime.
 *
 * Each pulse is a 3 px Energy-yellow dot; positions advance from firer
 * to target as ageFrac progresses, with the three pulses spaced 33 %
 * apart so all three are visible mid-flight.
 */
function drawPulseEnergy(
  ctx: CanvasRenderingContext2D,
  a: { x: number; y: number },
  b: { x: number; y: number },
  ageFrac: number,
): void {
  const alpha = 1 - ageFrac;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = ENERGY_YELLOW;
  // Three pulses staggered along the firing axis. Each one's progress
  // along firer→target is `ageFrac + offset`, clamped to [0,1].
  const offsets = [0, -0.2, -0.4];
  for (const off of offsets) {
    const t = ageFrac - off;
    if (t < 0 || t > 1) continue;
    const x = a.x + (b.x - a.x) * t;
    const y = a.y + (b.y - a.y) * t;
    ctx.beginPath();
    ctx.arc(x, y, 1.8, 0, Math.PI * 2);
    ctx.fill();
  }
  // Muzzle flash mirrors Beam's but smaller — quick blip per pulse.
  if (ageFrac < 0.4) {
    ctx.globalAlpha = (1 - ageFrac * 2.5) * alpha;
    ctx.beginPath();
    ctx.arc(a.x, a.y, 3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * Beam (Energy sub-type): instant gradient beam from firer (yellow) to
 * target (white), 2 px thick, fading over duration. Bright muzzle flash
 * at age 0 reads as the "shot leaving the gun". Distinct from Pulse
 * (which fires three quick flashes instead of one steady beam).
 *
 * GRAPHICS §7.1: "Solid beam, Energy-yellow → white gradient, 2 px thick,
 * 100 ms fade" + "Bright flash at mount".
 */
function drawBeamEnergy(
  ctx: CanvasRenderingContext2D,
  a: { x: number; y: number },
  b: { x: number; y: number },
  ageFrac: number,
  /** MFix26 — Mk-tier colour for the firer end of the gradient and the
   *  muzzle flash. Defaults to ENERGY_YELLOW (legacy single-color
   *  behaviour) so callers that don't have a weapon-name handy still
   *  work. The target end of the gradient stays white — reads as
   *  "beam terminates in heat" regardless of tier. */
  tierColour: string | null = null,
): void {
  const startColour = tierColour ?? ENERGY_YELLOW;
  const alpha = 1 - ageFrac;
  ctx.save();
  ctx.globalAlpha = alpha;
  // Beam — tier-colour → white gradient.
  const grad = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
  grad.addColorStop(0, startColour);
  grad.addColorStop(1, '#FFFFFF');
  ctx.strokeStyle = grad;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  // Muzzle flash — bright filled circle at firer for the first half of life.
  if (ageFrac < 0.5) {
    ctx.globalAlpha = (1 - ageFrac * 2) * alpha;
    ctx.fillStyle = startColour;
    ctx.beginPath();
    ctx.arc(a.x, a.y, 4, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * v4M4.f.3 — sprite-driven Pulse tracer (energy weapon family).
 * Returns true when the pulse-bolt sprite has loaded; false when
 * unavailable so the caller falls back to `drawPulseEnergy`
 * procedural. Minimal first-cut implementation — just renders the
 * bolt projectile travelling from firer to target with additive
 * blend. Charge-bloom / muzzle-flare / impact-burst / spark-ripple
 * sprites are loaded but not yet drawn — polish pass.
 *
 * Mk-tier tinting (Mk1 red → Mk3 orange → Mk5 yellow-white per
 * docs/WEAPON_VFX.md §3.2) is parked for the polish pass too —
 * tint compose with additive blend needs an offscreen-canvas pre-
 * tint step that isn't worth wiring for the first integration.
 * Sprites render in their neutral palette for now.
 */
function drawPulseSprite(
  ctx: CanvasRenderingContext2D,
  a: { x: number; y: number },
  b: { x: number; y: number },
  ageFrac: number,
  weaponName: string | undefined,
): boolean {
  const bolt = getWeaponVfxSprite('pulse-bolt');
  if (!bolt) return false;
  const boltFrame = bolt.frames[0];
  if (!boltFrame) return false;

  // Position along the firer→target axis, lerped by ageFrac so the
  // bolt visibly travels across the tracer lifetime. Matches the
  // existing drawPulseEnergy procedural's traversal feel.
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const x = a.x + dx * ageFrac;
  const y = a.y + dy * ageFrac;
  const angle = Math.atan2(dy, dx);
  const size = 18;

  // Mk tier — currently consumed for parse only (validates weaponName
  // path); tint application deferred to the v4M4.f.3 Pulse polish
  // pass per the function header note. `getMkTierColour` retained as
  // an unused-but-imported reference so the polish wire-up is a
  // single-line change. Reference satisfies noUnusedLocals.
  if (weaponName !== undefined) {
    void parseMkIndex(weaponName);
    void getMkTierColour;
  }

  withAdditiveBlend(ctx, () => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    drawTintedSprite(ctx, boltFrame, -size / 2, -size / 2, size, size, null);
    ctx.restore();
  });

  return true;
}

/**
 * v4M4.f.3 — sprite-driven Railgun tracer (kinetic family). Returns
 * true when the railgun-slug-streak sprite has loaded; false when
 * unavailable so the caller falls back to `drawRailgunKinetic`
 * procedural. Minimal first-cut implementation — renders only the
 * slug streak travelling from firer to target. charge-coil /
 * muzzle-discharge / impact-shockwave / impact-debris sprites load
 * via the cache but aren't drawn yet — polish pass.
 *
 * Mk-tier tinting deferred to the polish pass alongside Pulse
 * (sprites already lean toward Mk3-4 white-blue Expanse-canonical
 * palette so the untinted render is correct for the default tier).
 * Slug streak is the dominant Railgun visual — the procedural draws
 * already handled charge / impact via Hull-highlight effects which
 * compose acceptably alongside the slug sprite at tactical zoom.
 */
function drawRailgunSprite(
  ctx: CanvasRenderingContext2D,
  a: { x: number; y: number },
  b: { x: number; y: number },
  ageFrac: number,
  weaponName: string | undefined,
): boolean {
  const slug = getWeaponVfxSprite('railgun-slug-streak');
  if (!slug) return false;
  const slugFrame = slug.frames[0];
  if (!slugFrame) return false;

  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const x = a.x + dx * ageFrac;
  const y = a.y + dy * ageFrac;
  const angle = Math.atan2(dy, dx);
  const size = 22;

  // Mk tier parse retained for the polish wire-up — matches the
  // Pulse pattern (see drawPulseSprite for the rationale).
  if (weaponName !== undefined) {
    void parseMkIndex(weaponName);
    void getMkTierColour;
  }

  withAdditiveBlend(ctx, () => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    drawTintedSprite(ctx, slugFrame, -size / 2, -size / 2, size, size, null);
    ctx.restore();
  });

  return true;
}

/**
 * v4M4.f.3 — sprite-driven Torpedo tracer (Concussion family).
 * Returns true when the plasma-core sprite has loaded; false when
 * unavailable so the caller falls back to `drawTorpedoConcussion`
 * procedural. Two-layer composite per docs/WEAPON_VFX.md §2.5:
 * plasma-halo behind (wider, dimmer) + plasma-core on top (narrower,
 * brighter) for the canonical Photon Torpedo sustained-glow
 * projectile aesthetic.
 *
 * Position lerps along firer→target by ageFrac so the torpedo
 * visibly travels — the 12-tick TRACER_DURATION_BY_SUBTYPE.Torpedo
 * gives a slow, deliberate flight. launch-flash / impact-explosion
 * / shockwave-ring sprites load via the cache but aren't drawn yet
 * — polish pass.
 *
 * Mk-tier tinting deferred to the polish pass. The Torpedo cohort
 * accepted warm-orange-default identity (the Star Trek canonical
 * photon-torpedo look), so the orange-tinted sprites compose
 * acceptably without runtime tinting.
 */
function drawTorpedoSprite(
  ctx: CanvasRenderingContext2D,
  a: { x: number; y: number },
  b: { x: number; y: number },
  ageFrac: number,
  weaponName: string | undefined,
  /** MFix20.2 — current sim tick. Drives the torpedo-travel frame
   *  cycle via `pickAnimationFrame` with the sprite's authored
   *  ticksPerFrame (= 2) so the animation runs at a fixed cadence
   *  independent of flight duration. Pre-MFix20.2 the frame index
   *  came from `ageFrac * frameCount`, which stretched one full
   *  cycle across the entire flight — for typical 30-50 tick
   *  flights that was effectively static. */
  tick: number,
): boolean {
  // MFix15 — if the new 3-frame `torpedo-travel` sprite is loaded,
  // cycle through its frames during flight (replacing the static
  // plasma-core). Falls back to the v4M4.f single-frame
  // `torpedo-plasma-core` when travel isn't loaded (404 silent at
  // boot per the v4M4.a contract).
  //
  // MFix20.2 — frame selection switched from ageFrac-scaled to
  // tick-driven `pickAnimationFrame`. The sprite's meta
  // (ticksPerFrame: 2, mode: 'loop') gives a 6-tick loop (3 frames
  // × 2 ticks) at the 10 Hz sim rate, i.e. one full cycle every
  // 0.6 s — fast enough to read as a live plasma body rather than a
  // stretched single image, while still being slow enough to clearly
  // show each frame. Using `tick` as the elapsed counter is sound
  // because the sprite is in 'loop' mode — `pickAnimationFrame`
  // modulo-wraps the index, so absolute tick works the same as
  // relative-to-launch tick (the visible cycle is identical).
  const travel = getWeaponVfxSprite('torpedo-travel');
  let coreFrame: HTMLImageElement | undefined;
  let usingTravelSprite = false;
  if (travel && travel.frames.length > 0) {
    const frame = pickAnimationFrame(
      travel.frames,
      tick,
      travel.meta.ticksPerFrame,
      travel.meta.mode,
    );
    if (frame) {
      coreFrame = frame;
      usingTravelSprite = true;
    }
  }
  if (!coreFrame) {
    const core = getWeaponVfxSprite('torpedo-plasma-core');
    if (!core) return false;
    coreFrame = core.frames[0];
    if (!coreFrame) return false;
  }
  // MFix20 — `torpedo-plasma-halo` is the v4M4.f.1 procedurally-
  // generated near-white glow (RGB ~249, 1 % saturation). Painted
  // under the colored manual-cohort `torpedo-travel` with
  // `globalCompositeOperation = 'lighter'`, it raises every channel
  // to ~250 before the colored core paints — additive blend then
  // washes the red/orange torpedo body to white because each
  // channel is already saturated. The halo only matters on the
  // procedural fallback path (`torpedo-plasma-core` is also colorless
  // and the white halo gives it visible bulk). Skip the halo when
  // the colored travel sprite is the active core.
  const halo = usingTravelSprite ? null : getWeaponVfxSprite('torpedo-plasma-halo');
  const haloFrame = halo?.frames[0];

  // MFix20.3 — render position is `a` directly. The v4M4-era lerp
  // `a + (b-a)*ageFrac` was correct when callers passed (firer,
  // target) and wanted the lerp to advance the projectile along the
  // path. The v4M9.2.4 missile-loop caller bypassed that model and
  // started passing (screenPos, screenAhead) where screenPos is the
  // already-Bezier-evaluated missile position and screenAhead is a
  // tangent-direction probe 0.01 world units beyond. With ageFrac=0
  // (MFix15 era) the lerp was a no-op so the bug was latent; once
  // MFix20.1 wired the real ageFrac through, the lerp dragged each
  // torpedo up to ~8 screen px past its sim position as ageFrac
  // climbed toward 1 — multiple torpedoes at different ages each
  // drifted by different amounts and detonated visibly off-target.
  // Now: position from `a`, direction from `b - a`.
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const x = a.x;
  const y = a.y;
  const angle = Math.atan2(dy, dx);
  // MFix19 — bumped 14/28 → 24/40. The cropped travel sprites carry
  // recognisable detail (3-frame plasma body cycle) but at 14px the
  // downscaled result reads as a generic white circle in-game.
  const coreSize = 24;
  const haloSize = 40;
  // ageFrac is no longer consumed for position; the frame cycle is
  // tick-driven (see the `pickAnimationFrame` call above). Reference
  // satisfies noUnusedParameters; reinstate consumption if a future
  // polish pass restores an age-driven property (e.g. tint ramp).
  void ageFrac;

  // Mk tier parse retained for the polish wire-up — same pattern as
  // drawPulseSprite / drawRailgunSprite / drawBeamSprite.
  if (weaponName !== undefined) {
    void parseMkIndex(weaponName);
    void getMkTierColour;
  }

  withAdditiveBlend(ctx, () => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    // Halo first — wider/dimmer outer glow.
    if (haloFrame) {
      drawTintedSprite(
        ctx,
        haloFrame,
        -haloSize / 2,
        -haloSize / 2,
        haloSize,
        haloSize,
        null,
      );
    }
    // Plasma core on top — narrower/brighter projectile body.
    drawTintedSprite(
      ctx,
      coreFrame,
      -coreSize / 2,
      -coreSize / 2,
      coreSize,
      coreSize,
      null,
    );
    ctx.restore();
  });

  return true;
}

/**
 * MFix26 — sprite-driven Beam tracer (`drawBeamSprite`) deleted. The
 * v4M4.f sprite cohort never read as a beam-of-light despite multiple
 * iterations (MFix17 dimensions, MFix19 endpoint offsets, MFix20 trail
 * removal, MFix23 size taper, MFix24 tier tint wire-up, MFix25 halo-
 * only tint). The procedural `drawBeamEnergy` (above) is the active
 * render path and reads cleanly as a 2 px line with tier-colour
 * gradient. The `beam-core` / `beam-halo` / `beam-particle-trail`
 * sprites still load into the cache for future revival but aren't
 * drawn anywhere.
 *
 * If a future polish pass wants to bring back sprite-driven beams,
 * the deleted implementation is preserved at the MFix25 commit.
 */

/**
 * MFix27 — sprite-driven Burst tracer (`drawBurstSprite`) deleted.
 * Same rationale as MFix26 retiring `drawBeamSprite`: the v4M4.f
 * implementation inherited a volley-model render (internal offset
 * loop drew 7 staggered tracers inside a single call) that didn't
 * survive the v4M9.4 migration to per-projectile rendering. After
 * v4M9.4 the renderer calls the function once per projectile with
 * `(screenPos, screenAhead)` where screenAhead is only ~0.01 wu
 * beyond screenPos — the internal offset loop's 7 tracers all
 * collapsed to the same screen point, and the muzzle flash drew at
 * the projectile's current position rather than the firer's.
 *
 * The procedural `drawBurstKinetic` (above) is the active render
 * path: single round at the projectile's position with a motion
 * trail behind it. The gatling-stream effect emerges from many
 * projectiles in flight from the salvoSize-spawned cohort.
 *
 * burst-muzzle-flash / burst-tracer-streak / burst-impact-spark /
 * burst-casing-eject sprites still load into the cache for potential
 * future revival; deleted implementation preserved at the MFix26
 * commit.
 */

/**
 * MFix27 — Burst (Kinetic) per-projectile tracer.
 *
 * Originally written for the v4M4.f volley model: one call per fire-
 * event with `(firer, target)`, internal offset loop drew 3 staggered
 * rounds inside a single function call. After the v4M9.4 migration
 * Burst joined the persistent-projectile pipeline — each round in a
 * salvo became its own Projectile entry and the renderer iterates
 * `state.projectiles` calling this function ONCE PER ROUND with
 * `(screenPos, screenAhead)` where screenAhead is only ~0.01 world
 * units ahead of screenPos (tangent direction probe).
 *
 * The pre-MFix27 internal 3-round loop iterated offsets along (a, b)
 * — but with a and b virtually identical, all 3 collapsed to the
 * same screen position. The muzzle puff drew at `a` = projectile's
 * current position, not firer's, so flashes flickered wherever each
 * round was in flight (visible as "burst flashes near firer, near
 * target, occasionally in between").
 *
 * Rewrite renders ONE round at `a` with a short trail behind it
 * along the `b → a` axis. The gatling-stream effect emerges from
 * many projectiles in flight from the salvoSize-spawned cohort.
 * Muzzle flash removed — restoring it would require a WeaponFireEvent
 * path (like the other projectile families).
 */
function drawBurstKinetic(
  ctx: CanvasRenderingContext2D,
  a: { x: number; y: number },
  b: { x: number; y: number },
  ageFrac: number,
): void {
  // MFix28.1 — stripped down to a plain grey ball at the projectile's
  // position. Pre-MFix28.1 the render layered a motion-blur trail
  // (gradient stroke behind the dot along the travel axis) on top of
  // the dot; live-observation playtest of MFix28 still read as "small
  // streaks" rather than "rapid-fire bullets". Burst now matches the
  // design intent from Michael: basic grey ball, no tail, fired
  // directly at target in rapid succession. ageFrac + `b` are unused
  // under this single-frame circle render but stay in the signature
  // for call-site stability.
  //
  // MFix28.2 — radius tightened 3 → 2 and colour darkened from
  // HULL_HIGHLIGHT (`#C5CEDB`, light blue-grey that reads white at
  // small sizes) to a medium grey (`#7A8090`, ~40 % lightness) so
  // rounds register as kinetic projectiles rather than glowing
  // pellets. Distinct from Pulse's bright bolt aesthetic.
  void ageFrac;
  void b;
  ctx.save();
  ctx.fillStyle = '#7A8090';
  ctx.beginPath();
  ctx.arc(a.x, a.y, 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * Railgun (Kinetic sub-type): elongated hyper-velocity slug with a long
 * white-hot contrail. Reads as "single devastating shot" — distinct
 * from Burst's three smaller staggered projectiles.
 *
 * The slug is a 4 px capsule oriented along the beam axis; the contrail
 * extends 12 px back, fading from white-hot to transparent Hull-highlight.
 */
function drawRailgunKinetic(
  ctx: CanvasRenderingContext2D,
  a: { x: number; y: number },
  b: { x: number; y: number },
  ageFrac: number,
): void {
  const x = a.x + (b.x - a.x) * ageFrac;
  const y = a.y + (b.y - a.y) * ageFrac;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const trailLen = 12;
  ctx.save();
  // White-hot contrail — bright at the slug, fading back through
  // hull-highlight to fully transparent.
  const trail = ctx.createLinearGradient(x, y, x - ux * trailLen, y - uy * trailLen);
  trail.addColorStop(0, '#FFFFFF');
  trail.addColorStop(0.4, HULL_HIGHLIGHT);
  trail.addColorStop(1, 'rgba(197, 206, 219, 0)');
  ctx.strokeStyle = trail;
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x - ux * trailLen, y - uy * trailLen);
  ctx.stroke();
  // Elongated slug — 4 px line segment along beam axis, white-hot core.
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x - ux * 2, y - uy * 2);
  ctx.lineTo(x + ux * 2, y + uy * 2);
  ctx.stroke();
  // Muzzle flash — sharper than a generic kinetic puff, hints at the
  // mass-driver "crack". Hull-highlight, decays over first 25 % of life.
  if (ageFrac < 0.25) {
    ctx.globalAlpha = 1 - ageFrac * 4;
    ctx.fillStyle = HULL_HIGHLIGHT;
    ctx.beginPath();
    ctx.arc(a.x, a.y, 3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

// v4M9.2.6 — `drawMissileConcussion` and `drawTorpedoConcussion`
// (legacy v2M7.4c tracer-era draws) retired. Missile and Torpedo are
// both rendered from `state.projectiles` via `drawMissiles` /
// `drawTorpedoSprite` now; the tracer-style fallbacks are dead code.
// Removed wholesale rather than left behind under a `void` reference —
// the v4M9.2 design lock supersedes the tracer pipeline for both
// families.

/**
 * EMP: instant crackle beam at cyan. Per GRAPHICS §7.1: "Crackle beam,
 * #4FB3FF with 1-frame noise pattern, 3 px thick" + "Electrical arc"
 * muzzle. Crackle implemented as 3-segment polyline with midpoint
 * offsets perpendicular to the beam axis, displacement seeded from
 * `startTick` so the same fire renders the same crackle each frame
 * during its life (reproducible-under-replay).
 */
function drawEmpCrackle(
  ctx: CanvasRenderingContext2D,
  a: { x: number; y: number },
  b: { x: number; y: number },
  ageFrac: number,
  startTick: number,
): void {
  const alpha = 1 - ageFrac;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  // Perpendicular unit vector for the displacement.
  const px = -dy / len;
  const py = dx / len;
  // Cheap deterministic "noise" — hash startTick to a few offsets.
  const seed = startTick;
  const j1 = ((seed * 2654435761) >>> 0) / 0x100000000;
  const j2 = ((seed * 40503 + 13) >>> 0) / 0x100000000;
  const amp = Math.min(6, len * 0.05);
  const m1x = a.x + dx * 0.33 + px * (j1 - 0.5) * 2 * amp;
  const m1y = a.y + dy * 0.33 + py * (j1 - 0.5) * 2 * amp;
  const m2x = a.x + dx * 0.66 + px * (j2 - 0.5) * 2 * amp;
  const m2y = a.y + dy * 0.66 + py * (j2 - 0.5) * 2 * amp;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = EMP_CYAN;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(m1x, m1y);
  ctx.lineTo(m2x, m2y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  // Electrical-arc muzzle — small jagged dot at firer for the first
  // half of life.
  if (ageFrac < 0.5) {
    ctx.globalAlpha = (1 - ageFrac * 2) * alpha;
    ctx.fillStyle = EMP_CYAN;
    ctx.beginPath();
    ctx.arc(a.x, a.y, 3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * Cheap deterministic noise — mixes (seed, index) into a value in [0,1).
 * Replays match because shipId + startTick are both replay-stable, so a
 * given destruction frame yields the same shard fan every time.
 */
function hash01(seed: number, index: number): number {
  return ((((seed * 2654435761) ^ ((index * 40503 + 13) | 0)) >>> 0) / 0x100000000);
}

/**
 * Destruction sequence per GRAPHICS §4.5. Tick-time schedule:
 *   - f0           : 80 % alpha white flash overlay
 *   - f1–f3        : 4–6 polygon shards exploding radially at 60 px/s,
 *                    full opacity, spinning per-shard
 *   - f4–f9        : same shards continue outward but linearly fade
 *                    to 0 across 6 frames; Warning-amber afterglow
 *                    dots appear over the same window
 *   - f10          : `advanceVfx` filter drops the row
 *
 * Shard count, direction, speed jitter, spin and size jitter are all
 * derived from `hash01(seed, i)` with `seed = startTick ^ shipId`, so
 * a deterministic-replay against the same destruction tick produces
 * the identical shard fan.
 *
 * Shard size scales modestly with `classLevel` (small scout vs. big
 * capital), but radial speed stays fixed at 60 px/s per spec — so a
 * dreadnought's explosion has bigger shards but the same outward
 * reach as a frigate's.
 */
function drawDestruction(
  ctx: CanvasRenderingContext2D,
  c: { x: number; y: number },
  age: number,
  vfx: DestructionVfx,
): void {
  const ageSec = age / SIM_TICK_HZ;
  const seed = (vfx.startTick ^ vfx.shipId) | 0;
  // 4..6 shards inclusive.
  const shardCount = 4 + Math.floor(hash01(seed, 0) * 3);
  // Class-level size scaling matches CLASS_SCALE in ship-draw.ts.
  const sizeMul = 0.5 + vfx.classLevel * 0.25;

  // f0 — bright white flash overlay. Radius scales with class so a
  // capital's flash visibly outweighs a scout's.
  if (age === 0) {
    ctx.save();
    ctx.globalAlpha = 0.8;
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(c.x, c.y, 10 * sizeMul, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // f1–f9 — shard fan. Each shard travels outward at SHARD_SPEED_PX_PER_SEC
  // (with ±15 % per-shard jitter), spinning, fading after f3.
  if (age >= 1) {
    for (let i = 0; i < shardCount; i++) {
      const angle = hash01(seed, i * 4 + 1) * Math.PI * 2;
      const speedFactor = 0.85 + hash01(seed, i * 4 + 2) * 0.3;
      const spinRate = (hash01(seed, i * 4 + 3) - 0.5) * 6; // rad/s
      const sizeJitter = 0.7 + hash01(seed, i * 4 + 4) * 0.6;
      // Distance from centre — shards start at f1, so use (age - 1).
      const r = (SHARD_SPEED_PX_PER_SEC * speedFactor * (age - 1)) / SIM_TICK_HZ;
      const sx = c.x + Math.cos(angle) * r;
      const sy = c.y + Math.sin(angle) * r;
      const rot = angle + spinRate * ageSec;
      // Alpha — full f1–f3, linear fade f4 → 0 by f10.
      const alpha = age <= 3 ? 1 : Math.max(0, 1 - (age - 3) / 6);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(sx, sy);
      ctx.rotate(rot);
      ctx.fillStyle = vfx.accentColor;
      ctx.beginPath();
      const baseSize = 4 * sizeMul * sizeJitter;
      ctx.moveTo(-baseSize, -baseSize * 0.5);
      ctx.lineTo(baseSize, 0);
      ctx.lineTo(-baseSize * 0.5, baseSize * 0.7);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  }

  // f4–f9 — Warning-amber afterglow. Three small dots near the
  // centroid, fading with the same f4 → f10 ramp as the shards.
  if (age >= 4) {
    const afterglowAlpha = Math.max(0, 1 - (age - 3) / 6) * 0.7;
    ctx.save();
    ctx.globalAlpha = afterglowAlpha;
    ctx.fillStyle = WARNING_AMBER;
    for (let i = 0; i < 3; i++) {
      const ang = hash01(seed, i + 100) * Math.PI * 2;
      const dist = (6 + hash01(seed, i + 200) * 6) * sizeMul;
      ctx.beginPath();
      ctx.arc(c.x + Math.cos(ang) * dist, c.y + Math.sin(ang) * dist, 1.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}

/**
 * v3M28.6 (post-playtest tune) — multi-stage missile impact bloom.
 *
 * Three layers running concurrently over the 10-tick duration so
 * the impact reads as an actual detonation rather than a single PNG
 * flashing on:
 *
 *   1. **Initial flash** (t ∈ [0, 0.1]): bright white disc, peak
 *      brightness at t=0.03, alpha 0.6 → 0. The detonation punch —
 *      reads as "something just exploded here" before the bloom
 *      bitmap has had time to scale in.
 *   2. **PNG bloom scale-in** (t ∈ [0, 0.3]): the commissioned
 *      impact sprite scales 0 → peak with ease-out cubic.
 *   3. **Shockwave ring** (t ∈ [0.05, 0.5]): procedural circle
 *      stroke in family accent expanding from `peak*0.5` → `peak*1.6`
 *      with alpha 0.7 → 0. Adds the expanding-pressure-wave read.
 *   4. **PNG bloom alpha fade** (t ∈ [0.3, 1.0]): scale holds at
 *      peak; alpha fades 1.0 → 0 over the remaining 70%.
 *
 * Sprite-or-fallback gating: if the commissioned PNG hasn't loaded
 * yet (`getMissileSprite` returns null), a filled circle in the
 * family accent stands in for layer (2) so the impact still
 * registers visually before assets warm up. Layers (1) + (3) are
 * always procedural — no sprite dependency.
 */
function drawMissileImpact(
  ctx: CanvasRenderingContext2D,
  c: { x: number; y: number },
  age: number,
  vfx: MissileImpactVfx,
): void {
  const ageFrac = age / vfx.durationTicks; // 0..1
  const peakPx = MISSILE_IMPACT_PEAK_PX[vfx.family];
  const accent = MISSILE_IMPACT_FALLBACK_ACCENT[vfx.family];

  ctx.save();

  // ── Layer 1: initial white flash (0..0.1) ──────────────────────
  if (ageFrac < 0.1) {
    const flashT = ageFrac / 0.1; // 0..1 within the flash window
    // Peak at flashT=0.3, then fade. Triangle envelope.
    const flashIntensity =
      flashT < 0.3 ? flashT / 0.3 : Math.max(0, 1 - (flashT - 0.3) / 0.7);
    const flashAlpha = 0.6 * flashIntensity;
    const flashRadius = peakPx * 0.45;
    if (flashAlpha > 0) {
      ctx.globalAlpha = flashAlpha;
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(c.x, c.y, flashRadius, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // ── Layer 2: PNG bloom (or procedural fallback) ────────────────
  // Scale ramps 0 → 1.0 over [0, 0.3] (ease-out cubic), then holds.
  // Alpha is 1.0 until 0.3, then linear fades to 0 by t=1.0.
  const bloomScaleRaw = ageFrac < 0.3 ? ageFrac / 0.3 : 1.0;
  const bloomScale =
    ageFrac < 0.3 ? 1 - Math.pow(1 - bloomScaleRaw, 3) : 1.0;
  const bloomAlpha =
    ageFrac < 0.3 ? 1.0 : Math.max(0, 1 - (ageFrac - 0.3) / 0.7);
  if (bloomAlpha > 0 && bloomScale > 0) {
    const dia = peakPx * bloomScale;
    const radius = dia / 2;
    ctx.globalAlpha = bloomAlpha;
    // v4M9.2.6 — sprite resolution. Missile families key into the
    // commissioned v3M28.6 impact PNGs; Torpedo has no commissioned
    // PNG yet, so it falls straight through to the procedural-circle
    // fallback in the cyan-white accent.
    const spriteKey =
      vfx.family === 'zeon'
        ? 'zeon-impact'
        : vfx.family === 'pulson'
          ? 'pulson-impact'
          : null;
    const sprite = spriteKey !== null ? getMissileSprite(spriteKey) : null;
    if (sprite) {
      ctx.drawImage(sprite, c.x - radius, c.y - radius, dia, dia);
    } else {
      ctx.fillStyle = accent;
      ctx.beginPath();
      ctx.arc(c.x, c.y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // ── Layer 3: shockwave ring (0.05..0.5) ────────────────────────
  if (ageFrac >= 0.05 && ageFrac < 0.5) {
    const ringT = (ageFrac - 0.05) / 0.45; // 0..1 within the ring window
    const ringRadius = peakPx * (0.5 + ringT * 1.1); // 0.5 → 1.6 × peak/2 … wait, *peak* directly
    // ringRadius interprets `peakPx` as the diameter scale; ring radius runs
    // from `peakPx * 0.5` (i.e., the bloom edge at peak) outward to `peakPx * 1.6`.
    const ringAlpha = 0.7 * (1 - ringT);
    ctx.globalAlpha = ringAlpha;
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(c.x, c.y, ringRadius, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * Jump-out animation per GRAPHICS §8.1. 17-tick total duration broken
 * into four phases, all rendered in a heading-rotated frame so +x
 * points along the ship's direction of travel:
 *
 *   - f0–f2 : silhouette + engine plume; plume length grows 1× → 3×
 *             (linear in age) and tint shifts accent → yellow → white
 *   - f3–f4 : ship compresses to a horizontal line (width → 0,
 *             length stretches slightly to suggest forward draw)
 *   - f5    : line snaps to a bright white dot
 *   - f6–f16: dot fades and a Hull-highlight trail extending backward
 *             dissipates over 11 ticks (≈ 1 s per spec)
 *
 * Silhouette is approximated as a triangle (point along +x, base
 * trailing) rather than the full race-specific draw — keeps this
 * VFX self-contained and matches the lo-fi vector aesthetic.
 */
function drawJumpOut(
  ctx: CanvasRenderingContext2D,
  c: { x: number; y: number },
  age: number,
  vfx: JumpOutVfx,
): void {
  const sizeMul = 0.5 + vfx.classLevel * 0.25;
  const baseLen = 18 * sizeMul;
  const baseWid = 8 * sizeMul;
  ctx.save();
  ctx.translate(c.x, c.y);
  ctx.rotate(vfx.heading);
  if (age <= 2) {
    // Phase 1: engine plume elongates 1× → 3× and tint heats up.
    const t = age / 2;
    const plumeLen = baseLen * (1 + 2 * t);
    const plumeColor = t < 0.5 ? ENERGY_YELLOW : '#FFFFFF';
    // Silhouette — accent-coloured triangle pointing along +x.
    ctx.fillStyle = vfx.accentColor;
    ctx.beginPath();
    ctx.moveTo(baseLen / 2, 0);
    ctx.lineTo(-baseLen / 2, baseWid / 2);
    ctx.lineTo(-baseLen / 2, -baseWid / 2);
    ctx.closePath();
    ctx.fill();
    // Plume — gradient triangle pointing along -x, rooted at the tail.
    const plumeGrad = ctx.createLinearGradient(-baseLen / 2, 0, -baseLen / 2 - plumeLen, 0);
    plumeGrad.addColorStop(0, plumeColor);
    plumeGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = plumeGrad;
    ctx.beginPath();
    ctx.moveTo(-baseLen / 2, baseWid * 0.3);
    ctx.lineTo(-baseLen / 2 - plumeLen, 0);
    ctx.lineTo(-baseLen / 2, -baseWid * 0.3);
    ctx.closePath();
    ctx.fill();
  } else if (age <= 4) {
    // Phase 2: ship compresses to a line. Width → 0, length stretches.
    const t = (age - 2) / 2;
    const wid = baseWid * (1 - t);
    const len = baseLen * (1 + 0.4 * t);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(-len / 2, -wid / 2, len, wid);
  } else if (age === 5) {
    // Phase 3: line collapses to a bright dot.
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(0, 0, 4 * sizeMul, 0, Math.PI * 2);
    ctx.fill();
  } else {
    // Phase 4: dot fades, Hull-highlight trail dissipates.
    const t = (age - 5) / 11; // 0..1 across f6..f16
    const trailLen = baseLen * 1.5 * (1 - t);
    const alpha = 1 - t;
    ctx.globalAlpha = alpha;
    const trailGrad = ctx.createLinearGradient(0, 0, -trailLen, 0);
    trailGrad.addColorStop(0, HULL_HIGHLIGHT);
    trailGrad.addColorStop(1, 'rgba(197, 206, 219, 0)');
    ctx.strokeStyle = trailGrad;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(-trailLen, 0);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Jump-in animation per GRAPHICS §8.3. 6-tick body animation plus a
 * Shield-blue concentric ring that expands 4 → 16 px over 5 ticks
 * (0.5 s) and fades out. Phases:
 *
 *   - f0–f1 : bright white dot at arrival
 *   - f2–f3 : line expands along heading (white)
 *   - f4–f5 : silhouette resolves (accent-tinted triangle) + plume
 *             returns from 3× → 1× length
 *
 * The ring is drawn in screen-axis (untransformed) so it stays a
 * proper circle even at non-zero ship heading.
 */
function drawJumpIn(
  ctx: CanvasRenderingContext2D,
  c: { x: number; y: number },
  age: number,
  vfx: JumpInVfx,
): void {
  const sizeMul = 0.5 + vfx.classLevel * 0.25;
  const baseLen = 18 * sizeMul;
  const baseWid = 8 * sizeMul;

  // Concentric Shield-blue ring — expands and fades over 5 ticks.
  if (age < JUMP_IN_RING_DURATION_TICKS) {
    const ringT = age / JUMP_IN_RING_DURATION_TICKS;
    const ringR = (4 + 12 * ringT) * sizeMul;
    const ringAlpha = 1 - ringT;
    ctx.save();
    ctx.globalAlpha = ringAlpha;
    ctx.strokeStyle = SHIELD_BLUE;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(c.x, c.y, ringR, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // Body — heading-rotated.
  ctx.save();
  ctx.translate(c.x, c.y);
  ctx.rotate(vfx.heading);
  if (age <= 1) {
    // Bright dot.
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(0, 0, 4 * sizeMul, 0, Math.PI * 2);
    ctx.fill();
  } else if (age <= 3) {
    // Line expanding along heading (f2 = half, f3 = full).
    const t = (age - 1) / 2;
    const len = baseLen * t;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(-len / 2, -1.5 * sizeMul, len, 3 * sizeMul);
  } else {
    // Silhouette resolving + plume returning to baseline.
    const t = (age - 3) / 2; // 0.5..1
    ctx.fillStyle = vfx.accentColor;
    ctx.beginPath();
    ctx.moveTo(baseLen / 2, 0);
    ctx.lineTo(-baseLen / 2, baseWid / 2);
    ctx.lineTo(-baseLen / 2, -baseWid / 2);
    ctx.closePath();
    ctx.fill();
    // Plume — 3× length at f4, 1× at f5 (so it visibly retracts as
    // the ship "settles" out of warp).
    const plumeLen = baseLen * (3 - 2 * t);
    const plumeGrad = ctx.createLinearGradient(-baseLen / 2, 0, -baseLen / 2 - plumeLen, 0);
    plumeGrad.addColorStop(0, ENERGY_YELLOW);
    plumeGrad.addColorStop(1, 'rgba(255, 216, 77, 0)');
    ctx.fillStyle = plumeGrad;
    ctx.beginPath();
    ctx.moveTo(-baseLen / 2, baseWid * 0.3);
    ctx.lineTo(-baseLen / 2 - plumeLen, 0);
    ctx.lineTo(-baseLen / 2, -baseWid * 0.3);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

/**
 * v3M19 — floating damage-number text. Anchored at the target's
 * captured world position (transformed to screen at draw time), rises
 * a fixed pixel distance over its lifetime, fades on the last fraction
 * of duration. Drawn with a black stroke + colour fill so it stays
 * legible against starfield, planet discs, and ship glyphs alike.
 *
 * Stack offset: floaters from the same DamageEvent (shield + hull +
 * miss for one volley) share an anchor; `stackIndex` (0-based) pushes
 * each subsequent floater further up so they don't draw on top of
 * each other.
 */
function drawFloatingText(
  ctx: CanvasRenderingContext2D,
  c: { x: number; y: number },
  age: number,
  vfx: FloatingTextVfx,
): void {
  const ageFrac = age / vfx.durationTicks; // 0..1
  // Rise: 30 px over the full lifetime, eased linearly.
  const rise = ageFrac * 30;
  // Stack offset: 14 px per stack rank — clears single-line text comfortably.
  const stackOffset = vfx.stackIndex * 14;
  // Fade: full alpha until 60% of lifetime, ramp to 0 over the last 40%.
  const alpha = ageFrac < 0.6 ? 1 : Math.max(0, 1 - (ageFrac - 0.6) / 0.4);

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = 'bold 14px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Black stroke under the colour fill keeps the text legible against
  // bright/light backdrops (planet discs, shield halos).
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
  ctx.fillStyle = vfx.colour;
  const x = c.x;
  const y = c.y - rise - stackOffset;
  ctx.strokeText(vfx.text, x, y);
  ctx.fillText(vfx.text, x, y);
  ctx.restore();
}
